/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import * as d3 from 'd3';
import $ from 'jquery';
import {
  range,
  fill,
  zip,
  isNil,
  isEmpty,
  every,
  last,
  min,
  max,
  first,
  map,
  find,
  get
} from 'lodash';

let x = null;
let y = null;
let xAxisContainer = null;
let yAxisContainer = null;
let lineContainer = null;
let height = null;
let svg = null;
let divId = null;

function emptyPlot(min = 0, max = 100) {
  const x = range(min, max, (max - min) / 200);
  const y = fill(Array(200), 0);
  return zip(x, y);
}

function setOr(value, fallback) {
  if ((isNil(value) && isEmpty(value)) || value.length === 0) {
    console.error('NO DATA!!');
    return fallback;
  }
  return value;
}

const calculateWidth = (name, margin) =>{
  const bbox = d3.select('#flex').node().getBoundingClientRect();
    const actualWidth = name ==='OverallScore' ? 437 : 227;
    let cWidth = 0;
    switch(name){
      case 'Non-Dominant_Hand':
      case 'Dominant_Hand':
       cWidth = ((bbox.width - margin.left - margin.right)/100) * 25.8;
       return cWidth > actualWidth ? actualWidth : cWidth;
      default:
        return actualWidth
    }
}

export const create = function(settings) {
  const deferred = new $.Deferred();

  divId = settings.el;
  const margin = settings.margin;
  const transform = settings.transform;
  const parent = d3.select('#' + divId);

  // Add class to parent div so that our css selectors can be scoped
  parent.classed('distribution-chart', true);

  const width = calculateWidth(settings.el,margin);
  height = settings.el === 'OverallScore' ? 87 : 46;
  // modifyting width for antropometrics graph

  x = d3.scale
    .linear()
    .range([0, width])
    .clamp(true)
    .nice(10);

  y = d3.scale.linear().range([height, 0]);

  svg = parent
    .append('svg')
    .attr('overflow', 'visible')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + (transform? transform.y: margin.top) + ')');

  xAxisContainer = svg
    .append('g')
    .attr('class', 'x axis')
    .attr('font-family', 'GTAR')
    .attr('font-size', 14)
    .attr('font-size', width / 22)
    .attr('transform', 'translate(0,' + (height - 1) + ')');

  yAxisContainer = svg.append('g').attr('class', 'y axis');

  lineContainer = svg
    .append('g')
    .attr('class', 'line')
    .insert('path'); // TODO: look into replacing previous content

  svg
    .append('text')
    .attr('class', 'interpretation')
    .attr('x', width)
    .attr('y', -height / 2.7)
    .attr('fill', '#222222')
    .attr('text-anchor', 'end')
    .attr('font-family', 'GTAM')
    .attr('font-size', 16)
    .attr('font-wight', 500)
    .text('');

  // Rect indicator
  svg
    .append('rect')
    .attr('class', 'indicator')
    .attr('id', divId + '_indicator')
    .attr('x', 0)
    .attr('rx', 3)
    .attr('ry', 3)
    .attr('y', -1) // -1: Account for rounded corners
    .attr('width', 5)
    .attr('height', height)
    .attr('fill', '#000')
    // .moveToFront();

  // Outline
  svg
    .append('rect')
    .attr('class', 'outline')
    .attr('y', -3) // Don't cover the graph edge
    .attr('width', width)
    .attr('height', height + 3) // Don't cover the graph edge
    // .style("fill", "none")
    .attr('stroke-width', 3)
    .attr('stroke', '#000')
    .attr('fill', 'none');

  deferred.resolve('resolved');
  return deferred;
};


export const updateData = function(dataValue) {
  let density_plot = [
    [1.0179429401926423, 0.24085623516598578],
    [1.0223753904311612, 0.2507560732261962],
    [1.0268078406696801, 0.26095687163048154],
    [1.031240290908199, 0.27146242531169607],
    [1.035672741146718, 0.282276304273999],
    [1.0401051913852368, 0.2934018386283001],
    [1.0445376416237557, 0.3048421035249155],
    [1.0489700918622746, 0.3165999040167486],
    [1.0534025421007935, 0.3286777598878263],
    [1.0578349923393124, 0.3410778904834929],
    [1.0622674425778313, 0.35380219957996023],
    [1.0666998928163502, 0.3668522603322514],
    [1.0711323430548692, 0.38022930034082086],
    [1.075564793293388, 0.39393418687831233],
    [1.079997243531907, 0.4079674123189754],
    [1.0844296937704259, 0.4223290798142528],
    [1.0888621440089448, 0.4370188892588984],
    [1.0932945942474637, 0.45203612359274775],
    [1.0977270444859826, 0.4673796354838851],
    [1.1021594947245015, 0.4830478344394478],
    [1.1065919449630204, 0.4990386743906788],
    [1.1110243952015393, 0.5153496417990486],
    [1.1154568454400582, 0.5319777443303562],
    [1.119889295678577, 0.5489195001436261],
    [1.124321745917096, 0.5661709278413993],
    [1.128754196155615, 0.5837275371276042],
    [1.1331866463941338, 0.6015843202186468],
    [1.1376190966326527, 0.6197357440526213],
    [1.1420515468711716, 0.63817574334064],
    [1.1464839971096905, 0.6568977145032214],
    [1.1509164473482094, 0.675894510533408],
    [1.1553488975867283, 0.695158436826882],
    [1.1597813478252472, 0.7146812480177354],
    [1.1642137980637661, 0.7344541458567891],
    [1.168646248302285, 0.7544677781674032],
    [1.173078698540804, 0.7747122389116158],
    [1.1775111487793228, 0.7951770693971509],
    [1.1819435990178417, 0.8158512606534117],
    [1.1863760492563606, 0.8367232570019458],
    [1.1908084994948795, 0.8577809608441291],
    [1.1952409497333985, 0.8790117386859058],
    [1.1996733999719174, 0.9004024284163611],
    [1.2041058502104363, 0.9219393478537476],
    [1.2085383004489552, 0.9436083045692556],
    [1.212970750687474, 0.9653946069954183],
    [1.217403200925993, 0.9872830768225018],
    [1.2218356511645119, 1.009258062682621],
    [1.2262681014030308, 1.0313034551176015],
    [1.2307005516415497, 1.0534027028228465],
    [1.2351330018800686, 1.075538830155616],
    [1.2395654521185875, 1.097694455892243],
    [1.2439979023571064, 1.1198518132149002],
    [1.2484303525956253, 1.141992770904573],
    [1.2528628028341442, 1.1640988557129683],
    [1.257295253072663, 1.1861512758821318],
    [1.261727703311182, 1.2081309457766443],
    [1.266160153549701, 1.2300185115893834],
    [1.2705926037882198, 1.2517943780780125],
    [1.2750250540267387, 1.2734387362856054],
    [1.2794575042652576, 1.2949315921951337],
    [1.2838899545037765, 1.3162527962639776],
    [1.2883224047422954, 1.3373820737811477],
    [1.2927548549808143, 1.3582990559865837],
    [1.2971873052193332, 1.3789833118886832],
    [1.3016197554578521, 1.3994143807131965],
    [1.306052205696371, 1.419571804913748],
    [1.31048465593489, 1.4394351636715566],
    [1.3149171061734088, 1.4589841068094518],
    [1.3193495564119277, 1.478198389042991],
    [1.3237820066504467, 1.4970579044894339],
    [1.3282144568889656, 1.5155427213534918],
    [1.3326469071274845, 1.533633116707185],
    [1.3370793573660034, 1.551309611279799],
    [1.3415118076045223, 1.5685530041728502],
    [1.3459442578430412, 1.5853444074141463],
    [1.35037670808156, 1.6016652802644862],
    [1.354809158320079, 1.6174974631902705],
    [1.3592416085585979, 1.632823211415293],
    [1.3636740587971168, 1.6476252279652908],
    [1.3681065090356357, 1.661886696119391],
    [1.3725389592741546, 1.675591311183462],
    [1.3769714095126735, 1.6887233115015252],
    [1.3814038597511924, 1.7012675086228055],
    [1.3858363099897113, 1.7132093165437214],
    [1.3902687602282302, 1.7245347799460977],
    [1.394701210466749, 1.735230601355144],
    [1.399133660705268, 1.7452841671432746],
    [1.403566110943787, 1.7546835723086247],
    [1.4079985611823058, 1.7634176439601599],
    [1.4124310114208247, 1.7714759634445394],
    [1.4168634616593436, 1.7788488870534194],
    [1.4212959118978625, 1.7855275652535834],
    [1.4257283621363814, 1.7915039603862397],
    [1.4301608123749003, 1.7967708627859302],
    [1.4345932626134192, 1.8013219052738063],
    [1.4390257128519381, 1.805151575984492],
    [1.443458163090457, 1.8082552294903593],
    [1.447890613328976, 1.8106290961917937],
    [1.4523230635674949, 1.812270289946871],
    [1.4567555138060138, 1.8131768139188407],
    [1.4611879640445327, 1.8133475646248203],
    [1.4656204142830516, 1.812782334174219],
    [1.4700528645215705, 1.8114818106905262],
    [1.4744853147600894, 1.8094475769152762],
    [1.4789177649986083, 1.806682106998144],
    [1.4833502152371272, 1.8031887614822921],
    [1.487782665475646, 1.7989717804991938],
    [1.492215115714165, 1.7940362751922199],
    [1.496647565952684, 1.788388217393276],
    [1.5010800161912028, 1.7820344275816777],
    [1.5055124664297217, 1.774982561159241],
    [1.5099449166682406, 1.7672410930802616],
    [1.5143773669067595, 1.7588193008795645],
    [1.5188098171452784, 1.7497272461462046],
    [1.5232422673837973, 1.7399757544945993],
    [1.5276747176223162, 1.7295763940888913],
    [1.5321071678608351, 1.7185414527801721],
    [1.536539618099354, 1.706883913919788],
    [1.540972068337873, 1.6946174309153599],
    [1.5454045185763918, 1.6817563005992657],
    [1.5498369688149107, 1.6683154354822656],
    [1.5542694190534296, 1.6543103349675752],
    [1.5587018692919485, 1.6397570556030956],
    [1.5631343195304674, 1.6246721804516067],
    [1.5675667697689863, 1.6090727876605866],
    [1.5719992200075052, 1.5929764183148762],
    [1.5764316702460242, 1.576401043656678],
    [1.580864120484543, 1.5593650317583923],
    [1.585296570723062, 1.541887113734478],
    [1.5897290209615809, 1.5239863495789714],
    [1.5941614712000998, 1.505682093715418],
    [1.5985939214386187, 1.48699396034584],
    [1.6030263716771376, 1.4679417886849362],
    [1.6074588219156565, 1.4485456081650199],
    [1.6118912721541754, 1.4288256036962397],
    [1.6163237223926943, 1.4088020810654036],
    [1.6207561726312132, 1.3884954325552503],
    [1.625188622869732, 1.3679261028643028],
    [1.629621073108251, 1.347114555405466],
    [1.63405352334677, 1.3260812390593717],
    [1.6384859735852888, 1.3048465554560673],
    [1.6429184238238077, 1.2834308268560577],
    [1.6473508740623266, 1.2618542646989228],
    [1.6517833243008455, 1.2401369388847785],
    [1.6562157745393644, 1.2182987478507206],
    [1.6606482247778833, 1.1963593895011233],
    [1.6650806750164022, 1.1743383330472628],
    [1.6695131252549211, 1.1522547918081978],
    [1.67394557549344, 1.1301276970212268],
    [1.678378025731959, 1.1079756727065049],
    [1.6828104759704778, 1.0858170116266177],
    [1.6872429262089967, 1.0636696523780516],
    [1.6916753764475156, 1.0415511576475962],
    [1.6961078266860345, 1.0194786936627966],
    [1.7005402769245535, 0.9974690108616105],
    [1.7049727271630724, 0.9755384258025002],
    [1.7094051774015913, 0.9537028043322388],
    [1.7138376276401102, 0.9319775460248286],
    [1.718270077878629, 0.910377569901042],
    [1.722702528117148, 0.8889173014343102],
    [1.7271349783556669, 0.8676106608449338],
    [1.7315674285941858, 0.846471052680922],
    [1.7359998788327047, 0.8255113566802111],
    [1.7404323290712236, 0.8047439199055236],
    [1.7448647793097425, 0.7841805501397876],
    [1.7492972295482614, 0.7638325105267801],
    [1.7537296797867803, 0.7437105154385639],
    [1.7581621300252992, 0.723824727548303],
    [1.762594580263818, 0.7041847560842222],
    [1.767027030502337, 0.684799656237792],
    [1.771459480740856, 0.6656779296967094],
    [1.7758919309793748, 0.6468275262708759],
    [1.7803243812178937, 0.6282558465773901],
    [1.7847568314564126, 0.6099697457485435],
    [1.7891892816949315, 0.5919755381249575],
    [1.7936217319334504, 0.5742790028943229],
    [1.7980541821719693, 0.5568853906346962],
    [1.8024866324104882, 0.5397994307199819],
    [1.8069190826490071, 0.5230253395440716],
    [1.811351532887526, 0.5065668295191378],
    [1.815783983126045, 0.49042711880275913],
    [1.8202164333645638, 0.4746089417079317],
    [1.8246488836030827, 0.4591145597495337],
    [1.8290813338416017, 0.4439457732805078],
    [1.8335137840801206, 0.4291039336708819],
    [1.8379462343186395, 0.41458995598273846],
    [1.8423786845571584, 0.4004043320944134],
    [1.8468111347956773, 0.3865471442274899],
    [1.8512435850341962, 0.3730180788306008],
    [1.855676035272715, 0.35981644077460745],
    [1.860108485511234, 0.34694116781443374],
    [1.8645409357497529, 0.3343908452736294],
    [1.8689733859882718, 0.3221637209086742],
    [1.8734058362267907, 0.3102577199110549],
    [1.8778382864653096, 0.2986704600062737],
    [1.8822707367038285, 0.28739926661016596],
    [1.8867031869423474, 0.2764411880041946],
    [1.8911356371808663, 0.2657930104927661],
    [1.8955680874193852, 0.25545127350704416],
    [1.900000537657904, 0.24541228462123932]
  ];

  const visceral_plot = [
    [1, 0],
    [1.095, 0],
    [1.19, 0],
    [1.285, 0],
    [1.38, 0],
    [1.4749999999999999, 0],
    [1.5699999999999998, 0],
    [1.6649999999999998, 0],
    [1.7599999999999998, 0],
    [1.8549999999999998, 0],
    [1.9499999999999997, 0],
    [2.045, 0],
    [2.14, 0],
    [2.2350000000000003, 0],
    [2.3300000000000005, 0],
    [2.4250000000000007, 0],
    [2.520000000000001, 0],
    [2.615000000000001, 0],
    [2.7100000000000013, 0],
    [2.8050000000000015, 0],
    [2.9000000000000017, 0],
    [2.995000000000002, 0],
    [3.090000000000002, 0],
    [3.1850000000000023, 0],
    [3.2800000000000025, 0],
    [3.3750000000000027, 0],
    [3.470000000000003, 0],
    [3.565000000000003, 0],
    [3.6600000000000033, 0],
    [3.7550000000000034, 0],
    [3.8500000000000036, 0],
    [3.945000000000004, 0],
    [4.040000000000004, 0],
    [4.135000000000003, 0],
    [4.230000000000003, 0],
    [4.325000000000003, 0],
    [4.420000000000003, 0],
    [4.515000000000002, 0],
    [4.610000000000002, 0],
    [4.705000000000002, 0],
    [4.800000000000002, 0],
    [4.895000000000001, 0],
    [4.990000000000001, 0],
    [5.085000000000001, 0],
    [5.180000000000001, 0],
    [5.275, 0],
    [5.37, 0],
    [5.465, 0],
    [5.56, 0],
    [5.654999999999999, 0],
    [5.749999999999999, 0],
    [5.844999999999999, 0],
    [5.939999999999999, 0],
    [6.034999999999998, 0],
    [6.129999999999998, 0],
    [6.224999999999998, 0],
    [6.319999999999998, 0],
    [6.414999999999997, 0],
    [6.509999999999997, 0],
    [6.604999999999997, 0],
    [6.699999999999997, 0],
    [6.794999999999996, 0],
    [6.889999999999996, 0],
    [6.984999999999996, 0],
    [7.079999999999996, 0],
    [7.174999999999995, 0],
    [7.269999999999995, 0],
    [7.364999999999995, 0],
    [7.459999999999995, 0],
    [7.554999999999994, 0],
    [7.649999999999994, 0],
    [7.744999999999994, 0],
    [7.839999999999994, 0],
    [7.934999999999993, 0],
    [8.029999999999994, 0],
    [8.124999999999995, 0],
    [8.219999999999995, 0],
    [8.314999999999996, 0],
    [8.409999999999997, 0],
    [8.504999999999997, 0],
    [8.599999999999998, 0],
    [8.694999999999999, 0],
    [8.79, 0],
    [8.885, 0],
    [8.98, 0],
    [9.075000000000001, 0],
    [9.170000000000002, 0],
    [9.265000000000002, 0],
    [9.360000000000003, 0],
    [9.455000000000004, 0],
    [9.550000000000004, 0],
    [9.645000000000005, 0],
    [9.740000000000006, 0],
    [9.835000000000006, 0],
    [9.930000000000007, 0],
    [10.025000000000007, 0],
    [10.120000000000008, 0],
    [10.215000000000009, 0],
    [10.31000000000001, 0],
    [10.40500000000001, 0],
    [10.50000000000001, 0],
    [10.595000000000011, 0],
    [10.690000000000012, 0],
    [10.785000000000013, 0],
    [10.880000000000013, 0],
    [10.975000000000014, 0],
    [11.070000000000014, 0],
    [11.165000000000015, 0],
    [11.260000000000016, 0],
    [11.355000000000016, 0],
    [11.450000000000017, 0],
    [11.545000000000018, 0],
    [11.640000000000018, 0],
    [11.735000000000019, 0],
    [11.83000000000002, 0],
    [11.92500000000002, 0],
    [12.020000000000021, 0],
    [12.115000000000022, 0],
    [12.210000000000022, 0],
    [12.305000000000023, 0],
    [12.400000000000023, 0],
    [12.495000000000024, 0],
    [12.590000000000025, 0],
    [12.685000000000025, 0],
    [12.780000000000026, 0],
    [12.875000000000027, 0],
    [12.970000000000027, 0],
    [13.065000000000028, 0],
    [13.160000000000029, 0],
    [13.25500000000003, 0],
    [13.35000000000003, 0],
    [13.44500000000003, 0],
    [13.540000000000031, 0],
    [13.635000000000032, 0],
    [13.730000000000032, 0],
    [13.825000000000033, 0],
    [13.920000000000034, 0],
    [14.015000000000034, 0],
    [14.110000000000035, 0],
    [14.205000000000036, 0],
    [14.300000000000036, 0],
    [14.395000000000037, 0],
    [14.490000000000038, 0],
    [14.585000000000038, 0],
    [14.680000000000039, 0],
    [14.77500000000004, 0],
    [14.87000000000004, 0],
    [14.96500000000004, 0],
    [15.060000000000041, 0],
    [15.155000000000042, 0],
    [15.250000000000043, 0],
    [15.345000000000043, 0],
    [15.440000000000044, 0],
    [15.535000000000045, 0],
    [15.630000000000045, 0],
    [15.725000000000046, 0],
    [15.820000000000046, 0],
    [15.915000000000047, 0],
    [16.010000000000048, 0],
    [16.105000000000047, 0],
    [16.200000000000045, 0],
    [16.295000000000044, 0],
    [16.390000000000043, 0],
    [16.485000000000042, 0],
    [16.58000000000004, 0],
    [16.67500000000004, 0],
    [16.77000000000004, 0],
    [16.865000000000038, 0],
    [16.960000000000036, 0],
    [17.055000000000035, 0],
    [17.150000000000034, 0],
    [17.245000000000033, 0],
    [17.340000000000032, 0],
    [17.43500000000003, 0],
    [17.53000000000003, 0],
    [17.62500000000003, 0],
    [17.720000000000027, 0],
    [17.815000000000026, 0],
    [17.910000000000025, 0],
    [18.005000000000024, 0],
    [18.100000000000023, 0],
    [18.19500000000002, 0],
    [18.29000000000002, 0],
    [18.38500000000002, 0],
    [18.480000000000018, 0],
    [18.575000000000017, 0],
    [18.670000000000016, 0],
    [18.765000000000015, 0],
    [18.860000000000014, 0],
    [18.955000000000013, 0],
    [19.05000000000001, 0],
    [19.14500000000001, 0],
    [19.24000000000001, 0],
    [19.335000000000008, 0],
    [19.430000000000007, 0],
    [19.525000000000006, 0],
    [19.620000000000005, 0],
    [19.715000000000003, 0],
    [19.810000000000002, 0],
    [19.905, 0]
  ];
  const mental_plot = [
    [0, 0.3184435033184359],
    [0.07035175879396985, 0.32058135385612574],
    [0.1407035175879397, 0.31768916008794845],
    [0.21105527638190955, 0.3104401022729612],
    [0.2814070351758794, 0.29976526342442017],
    [0.3517587939698492, 0.28674027674014513],
    [0.4221105527638191, 0.272459943778976],
    [0.49246231155778897, 0.2579194154532768],
    [0.5628140703517588, 0.243918984216871],
    [0.6331658291457286, 0.2310050502435939],
    [0.7035175879396984, 0.21945329446813056],
    [0.7738693467336684, 0.20929278736904508],
    [0.8442211055276382, 0.20036305676787164],
    [0.914572864321608, 0.19239122772308945],
    [0.9849246231155779, 0.18507402912201854],
    [1.0552763819095476, 0.17814999983589705],
    [1.1256281407035176, 0.17145033876650492],
    [1.1959798994974875, 0.16492178547427236],
    [1.2663316582914572, 0.1586206606426561],
    [1.3366834170854272, 0.15268263100476043],
    [1.4070351758793969, 0.14727691751832767],
    [1.4773869346733668, 0.14255586767681366],
    [1.5477386934673367, 0.13861079712207616],
    [1.6180904522613064, 0.13544293224987158],
    [1.6884422110552764, 0.1329546823741324],
    [1.7587939698492463, 0.1309621127982776],
    [1.829145728643216, 0.12922524453334808],
    [1.899497487437186, 0.1274894642932514],
    [1.9698492462311559, 0.12552946696249026],
    [2.040201005025126, 0.12318704029849026],
    [2.1105527638190953, 0.12039556479893647],
    [2.180904522613065, 0.1171869567341544],
    [2.251256281407035, 0.11368032244396625],
    [2.321608040201005, 0.11005511159279943],
    [2.391959798994975, 0.10651438912981326],
    [2.4623115577889445, 0.10324548528499082],
    [2.5326633165829144, 0.1003854746169901],
    [2.6030150753768844, 0.09799770915050411],
    [2.6733668341708543, 0.09606328354954377],
    [2.7437185929648242, 0.09448833625343962],
    [2.8140703517587937, 0.0931250789396439],
    [2.8844221105527637, 0.09180196671624441],
    [2.9547738693467336, 0.09035691599129901],
    [3.0251256281407035, 0.08866718516881726],
    [3.0954773869346734, 0.08667045502329974],
    [3.1658291457286434, 0.08437355212666492],
    [3.236180904522613, 0.08184774622985466],
    [3.306532663316583, 0.0792121261678486],
    [3.3768844221105527, 0.0766087302071983],
    [3.4472361809045227, 0.0741744858168628],
    [3.5175879396984926, 0.07201537817140827],
    [3.587939698492462, 0.0701875955848562],
    [3.658291457286432, 0.0686888688253173],
    [3.728643216080402, 0.06746115871561263],
    [3.798994974874372, 0.06640366560629196],
    [3.869346733668342, 0.06539325061144702],
    [3.9396984924623117, 0.06430811147011745],
    [4.010050251256281, 0.06305014904088968],
    [4.080402010050252, 0.06156192943136399],
    [4.150753768844221, 0.05983536072015047],
    [4.221105527638191, 0.057910897299665545],
    [4.291457286432161, 0.05586791657473344],
    [4.36180904522613, 0.053808530452670915],
    [4.432160804020101, 0.05183820383241151],
    [4.50251256281407, 0.050046970673265204],
    [4.57286432160804, 0.04849472126143231],
    [4.64321608040201, 0.047203078815175964],
    [4.71356783919598, 0.04615500166889485],
    [4.78391959798995, 0.045301720599112236],
    [4.8542713567839195, 0.04457524427982979],
    [4.924623115577889, 0.043903691763133225],
    [4.994974874371859, 0.0432263042234078],
    [5.065326633165829, 0.04250520000221246],
    [5.135678391959799, 0.04173170012569163],
    [5.206030150753769, 0.04092619839157384],
    [5.276381909547738, 0.04013184824132956],
    [5.346733668341709, 0.03940353617200522],
    [5.417085427135678, 0.03879448502179918],
    [5.4874371859296485, 0.03834322563076511],
    [5.557788944723618, 0.03806353299412039],
    [5.628140703517587, 0.03793928710480911],
    [5.698492462311558, 0.03792522489821199],
    [5.768844221105527, 0.037953397209596944],
    [5.839195979899498, 0.037944056810732485],
    [5.909547738693467, 0.03781888525601265],
    [5.9798994974874375, 0.03751406548074207],
    [6.050251256281407, 0.03699078844359377],
    [6.1206030150753765, 0.03624131654431495],
    [6.190954773869347, 0.03528959943017006],
    [6.261306532663316, 0.034186473259918575],
    [6.331658291457287, 0.033000471105075126],
    [6.402010050251256, 0.03180604263298393],
    [6.472361809045226, 0.030671388037072346],
    [6.542713567839196, 0.0296480913646649],
    [6.613065326633166, 0.028764312673922925],
    [6.683417085427136, 0.028022563827167692],
    [6.7537688442211055, 0.02740220057134103],
    [6.824120603015075, 0.026865888310716343],
    [6.894472361809045, 0.026368603958780018],
    [6.964824120603015, 0.025867343310797264],
    [7.035175879396985, 0.025329671879669655],
    [7.105527638190955, 0.024739575820938248],
    [7.175879396984924, 0.02409966199746286],
    [7.2462311557788945, 0.0234294981931204],
    [7.316582914572864, 0.02276062949386158],
    [7.386934673366834, 0.022129414606735662],
    [7.457286432160804, 0.02156918822142162],
    [7.527638190954773, 0.021103315194342265],
    [7.597989949748744, 0.020740461458035802],
    [7.668341708542713, 0.02047292358335164],
    [7.738693467336684, 0.02027823531580927],
    [7.809045226130653, 0.020123628909627215],
    [7.8793969849246235, 0.019972393699774018],
    [7.949748743718593, 0.019790841629984565],
    [8.020100502512562, 0.01955451514349556],
    [8.090452261306533, 0.019252461951846343],
    [8.160804020100503, 0.018888808797099244],
    [8.231155778894472, 0.018481407668182008],
    [8.301507537688442, 0.01805789572232786],
    [8.371859296482413, 0.017649995524889515],
    [8.442211055276381, 0.01728719533518946],
    [8.512562814070352, 0.016991035661750892],
    [8.582914572864322, 0.016771078447291966],
    [8.65326633165829, 0.01662328438403436],
    [8.72361809045226, 0.0165310444155167],
    [8.793969849246231, 0.016468598140771812],
    [8.864321608040202, 0.016406123045841013],
    [8.93467336683417, 0.016315477760305775],
    [9.00502512562814, 0.016175483091600794],
    [9.075376884422111, 0.015975740087855926],
    [9.14572864321608, 0.01571828735691213],
    [9.21608040201005, 0.015416827983356342],
    [9.28643216080402, 0.015093725136357155],
    [9.35678391959799, 0.014775384817531687],
    [9.42713567839196, 0.014486935876658164],
    [9.49748743718593, 0.014247229484748065],
    [9.5678391959799, 0.014065096283635259],
    [9.638190954773869, 0.013937540682188547],
    [9.708542713567839, 0.013850172056281237],
    [9.77889447236181, 0.013779747136589493],
    [9.849246231155778, 0.013698308295680372],
    [9.919597989949748, 0.013578121150500184],
    [9.989949748743719, 0.013396491450960543],
    [10.06030150753769, 0.01313959362652243],
    [10.130653266331658, 0.012804655583312743],
    [10.201005025125628, 0.012400170523170115],
    [10.271356783919598, 0.011944180844355806],
    [10.341708542713567, 0.011461029151945329],
    [10.412060301507537, 0.010977232249349938],
    [10.482412060301508, 0.010517260676480695],
    [10.552763819095476, 0.010099981427656906],
    [10.623115577889447, 0.009736357035912926],
    [10.693467336683417, 0.009428728157926145],
    [10.763819095477388, 0.009171694284936206],
    [10.834170854271356, 0.00895430926641635],
    [10.904522613065327, 0.008763080051963011],
    [10.974874371859297, 0.00858513757793203],
    [11.045226130653266, 0.008410954810796331],
    [11.115577889447236, 0.008236111062710773],
    [11.185929648241206, 0.008061814618545481],
    [11.256281407035175, 0.00789415308559063],
    [11.326633165829145, 0.007742292066004303],
    [11.396984924623116, 0.007616040488709283],
    [11.467336683417086, 0.00752330988734059],
    [11.537688442211055, 0.0074679977139583],
    [11.608040201005025, 0.007448724587152735],
    [11.678391959798995, 0.007458674571796779],
    [11.748743718592964, 0.0074865630512759],
    [11.819095477386934, 0.00751853260064794],
    [11.889447236180905, 0.00754059647067623],
    [11.959798994974875, 0.007541145665581085],
    [12.030150753768844, 0.0075130276402337924],
    [12.100502512562814, 0.007454792312313588],
    [12.170854271356784, 0.0073708660892464984],
    [12.241206030150753, 0.007270624134452731],
    [12.311557788944723, 0.007166544245254029],
    [12.381909547738694, 0.007071801410784185],
    [12.452261306532662, 0.006997766960221624],
    [12.522613065326633, 0.006951890762045636],
    [12.592964824120603, 0.0069363672691481046],
    [12.663316582914574, 0.006947832075272291],
    [12.733668341708542, 0.006978135285134669],
    [12.804020100502512, 0.007016030199458573],
    [12.874371859296483, 0.007049440469573497],
    [12.944723618090451, 0.007067859369132296],
    [13.015075376884422, 0.007064411731912995],
    [13.085427135678392, 0.007037176334500005],
    [13.15577889447236, 0.006989510686035549],
    [13.226130653266331, 0.006929313309484019],
    [13.296482412060302, 0.006867363446699798],
    [13.366834170854272, 0.0068150554601616765],
    [13.43718592964824, 0.0067819613163890655],
    [13.507537688442211, 0.006773687641123474],
    [13.577889447236181, 0.006790438193226979],
    [13.64824120603015, 0.006826559490882252],
    [13.71859296482412, 0.00687116273325648],
    [13.78894472361809, 0.006909714673716029],
    [13.859296482412061, 0.006926311937032701],
    [13.92964824120603, 0.006906230880899418],
    [14, 0.006838300732982176]
  ];
  const vegetable_plot = [
    [1.056355, 0.5621427243941393],
    [1.0658842038689553, 0.5760172716202843],
    [1.0754134077379107, 0.589605732126877],
    [1.0849426116068661, 0.6028997475959174],
    [1.0944718154758215, 0.6158935897092093],
    [1.104001019344777, 0.6285840402961722],
    [1.1135302232137323, 0.6409702342802036],
    [1.1230594270826877, 0.6530534685046551],
    [1.132588630951643, 0.6648369801243831],
    [1.1421178348205985, 0.6763256987760334],
    [1.1516470386895539, 0.6875259771789348],
    [1.1611762425585093, 0.6984453051595628],
    [1.1707054464274647, 0.7090920123298987],
    [1.18023465029642, 0.7194749647795535],
    [1.1897638541653754, 0.729603261162224],
    [1.1992930580343308, 0.7394859334701679],
    [1.2088222619032862, 0.7491316575999062],
    [1.2183514657722416, 0.7585484785246267],
    [1.227880669641197, 0.7677435545123051],
    [1.2374098735101524, 0.7767229243736895],
    [1.2469390773791078, 0.7854913012032098],
    [1.2564682812480632, 0.7940518955016391],
    [1.2659974851170186, 0.8024062699562201],
    [1.275526688985974, 0.8105542275164277],
    [1.2850558928549294, 0.8184937337564148],
    [1.2945850967238848, 0.8262208738725573],
    [1.3041143005928402, 0.8337298440405074],
    [1.3136435044617956, 0.8410129762628095],
    [1.323172708330751, 0.848060795287745],
    [1.3327019121997064, 0.8548621056815171],
    [1.3422311160686617, 0.8614041066982261],
    [1.3517603199376171, 0.867672532221025],
    [1.3612895238065725, 0.8736518127480883],
    [1.370818727675528, 0.8793252561708607],
    [1.3803479315444833, 0.8846752439402173],
    [1.3898771354134387, 0.8896834391370828],
    [1.399406339282394, 0.8943310029550479],
    [1.4089355431513495, 0.8985988161591455],
    [1.418464747020305, 0.9024677022010018],
    [1.4279939508892603, 0.9059186488400753],
    [1.4375231547582157, 0.9089330253350629],
    [1.447052358627171, 0.9114927925214602],
    [1.4565815624961265, 0.9135807033716683],
    [1.4661107663650819, 0.9151804919346818],
    [1.4756399702340373, 0.9162770488650848],
    [1.4851691741029927, 0.916856582067493],
    [1.494698377971948, 0.9169067612960005],
    [1.5042275818409034, 0.9164168458514701],
    [1.5137567857098588, 0.9153777948074748],
    [1.5232859895788142, 0.9137823594633913],
    [1.5328151934477696, 0.9116251579669461],
    [1.542344397316725, 0.9089027322658096],
    [1.5518736011856804, 0.9056135877371524],
    [1.5614028050546358, 0.9017582160045897],
    [1.5709320089235912, 0.8973391015840751],
    [1.5804612127925466, 0.8923607131053756],
    [1.589990416661502, 0.8868294799350556],
    [1.5995196205304574, 0.880753755083639],
    [1.6090488243994128, 0.8741437653156682],
    [1.6185780282683682, 0.8670115494008641],
    [1.6281072321373236, 0.8593708854498874],
    [1.637636436006279, 0.8512372082733292],
    [1.6471656398752343, 0.8426275176901811],
    [1.6566948437441897, 0.8335602786954385],
    [1.6662240476131451, 0.8240553143781256],
    [1.6757532514821005, 0.8141336924631678],
    [1.685282455351056, 0.8038176063350102],
    [1.6948116592200113, 0.7931302513888356],
    [1.7043408630889667, 0.7820956975475243],
    [1.713870066957922, 0.7707387587792843],
    [1.7233992708268775, 0.7590848604519582],
    [1.7329284746958329, 0.7471599053646565],
    [1.7424576785647883, 0.7349901393044923],
    [1.7519868824337437, 0.7226020169843991],
    [1.761516086302699, 0.7100220692256468],
    [1.7710452901716545, 0.6972767722539781],
    [1.7805744940406099, 0.6843924199792848],
    [1.7901036979095653, 0.6713950001239063],
    [1.7996329017785206, 0.6583100750517834],
    [1.809162105647476, 0.6451626681289439],
    [1.8186913095164314, 0.6319771564132807],
    [1.8282205133853868, 0.6187771704280247],
    [1.8377497172543422, 0.6055855017175809],
    [1.8472789211232976, 0.5924240188170756],
    [1.856808124992253, 0.5793135921877091],
    [1.8663373288612084, 0.566274028580129],
    [1.8758665327301638, 0.5533240151883649],
    [1.8853957365991192, 0.540481073849156],
    [1.8949249404680746, 0.527761525427493],
    [1.90445414433703, 0.5151804644112267],
    [1.9139833482059854, 0.5027517436178452],
    [1.9235125520749408, 0.4904879687977525],
    [1.9330417559438962, 0.47840050280283924],
    [1.9425709598128515, 0.4664994788797071],
    [1.952100163681807, 0.45479382254577905],
    [1.9616293675507623, 0.44329128141592516],
    [1.9711585714197177, 0.4319984622693525],
    [1.9806877752886731, 0.42092087458274213],
    [1.9902169791576285, 0.41006297970732236],
    [1.999746183026584, 0.39942824483572636],
    [2.0092753868955393, 0.3890192008892429],
    [2.0188045907644945, 0.37883750345759076],
    [2.02833379463345, 0.3688839959410931],
    [2.0378629985024057, 0.35915877407799046],
    [2.047392202371361, 0.34966125108656076],
    [2.056921406240316, 0.3403902227107063],
    [2.0664506101092717, 0.33134393152696795],
    [2.0759798139782273, 0.3225201299480717],
    [2.0855090178471825, 0.3139161414409654],
    [2.0950382217161376, 0.3055289195630066],
    [2.1045674255850932, 0.29735510450634384],
    [2.114096629454049, 0.2893910769249819],
    [2.123625833323004, 0.2816330088990864],
    [2.133155037191959, 0.2740769119650174],
    [2.142684241060915, 0.26671868220513173],
    [2.1522134449298704, 0.25955414244731917],
    [2.1617426487988256, 0.25257908166921406],
    [2.1712718526677808, 0.24578929173533076],
    [2.1808010565367364, 0.23918060161658497],
    [2.190330260405692, 0.2327489092507792],
    [2.199859464274647, 0.22649021120013865],
    [2.2093886681436024, 0.22040063024851433],
    [2.218917872012558, 0.21447644105772262],
    [2.2284470758815136, 0.20871409397088017],
    [2.2379762797504688, 0.20311023701235842],
    [2.247505483619424, 0.1976617360908529],
    [2.2570346874883795, 0.19236569336608453],
    [2.266563891357335, 0.18721946369284403],
    [2.2760930952262903, 0.18222066901054895],
    [2.2856222990952455, 0.17736721050415255],
    [2.295151502964201, 0.17265727832510464],
    [2.3046807068331567, 0.1680893586307255],
    [2.314209910702112, 0.16366223767841953],
    [2.323739114571067, 0.15937500269884336],
    [2.3332683184400227, 0.15522703927042122],
    [2.3427975223089783, 0.1512180249271202],
    [2.3523267261779335, 0.14734791875254713],
    [2.3618559300468887, 0.14361694674614525],
    [2.3713851339158443, 0.14002558279129396],
    [2.3809143377848, 0.13657452510977627],
    [2.390443541653755, 0.13326466815145596],
    [2.3999727455227102, 0.13009706994089967],
    [2.409501949391666, 0.1270729149826206],
    [2.4190311532606215, 0.12419347291192796],
    [2.4285603571295766, 0.12146005316727061],
    [2.438089560998532, 0.1188739560504119],
    [2.4476187648674874, 0.11643642063077486],
    [2.457147968736443, 0.11414857003771975],
    [2.466677172605398, 0.11201135476727832],
    [2.4762063764743534, 0.11002549470590635],
    [2.485735580343309, 0.10819142064117186],
    [2.4952647842122646, 0.10650921608610331],
    [2.50479398808122, 0.1049785602885638],
    [2.514323191950175, 0.10359867332793854],
    [2.5238523958191306, 0.10236826421749914],
    [2.533381599688086, 0.10128548293092945],
    [2.5429108035570414, 0.10034787725514911],
    [2.5524400074259965, 0.09955235533821909],
    [2.561969211294952, 0.09889515475085635],
    [2.5714984151639078, 0.09837181881310482],
    [2.581027619032863, 0.09797718085473699],
    [2.590556822901818, 0.09770535697990877],
    [2.6000860267707737, 0.09754974779480016],
    [2.6096152306397293, 0.09750304943307332],
    [2.6191444345086845, 0.0975572740798915],
    [2.6286736383776397, 0.0977037800531625],
    [2.6382028422465953, 0.09793331135302491],
    [2.647732046115551, 0.09823604644001484],
    [2.657261249984506, 0.0986016558515642],
    [2.6667904538534613, 0.09901936811839936],
    [2.676319657722417, 0.09947804329982794],
    [2.6858488615913725, 0.09996625332274825],
    [2.6953780654603277, 0.10047236818620536],
    [2.704907269329283, 0.10098464698403489],
    [2.7144364731982384, 0.10149133260500381],
    [2.723965677067194, 0.1019807488948979],
    [2.7334948809361492, 0.10244139901004573],
    [2.7430240848051044, 0.10286206365812169],
    [2.75255328867406, 0.1032318979106776],
    [2.7620824925430156, 0.10354052528325626],
    [2.771611696411971, 0.10377812781302337],
    [2.781140900280926, 0.10393553092024548],
    [2.7906701041498816, 0.10400428191756463],
    [2.800199308018837, 0.10397672112848715],
    [2.8097285118877924, 0.10384604469187232],
    [2.8192577157567476, 0.10360635826023226],
    [2.828786919625703, 0.10325272094363487],
    [2.838316123494659, 0.10278117900506366],
    [2.847845327363614, 0.10218878897403266],
    [2.857374531232569, 0.10147363000984691],
    [2.8669037351015247, 0.1006348055107481],
    [2.8764329389704804, 0.09967243412701284],
    [2.8859621428394355, 0.09858763049165259],
    [2.8954913467083907, 0.09738247612868386],
    [2.9050205505773463, 0.09605998113324969],
    [2.914549754446302, 0.09462403733773414],
    [2.924078958315257, 0.0930793637814147],
    [2.9336081621842123, 0.09143144538650148],
    [2.943137366053168, 0.08968646580950758],
    [2.9526665699221235, 0.08785123548310829]
  ];
  const added_sugars_plot = [
    [10.90784872, 0.046870914288573],
    [11.120054566925633, 0.04924993740715776],
    [11.332260413851266, 0.051413360611939615],
    [11.544466260776899, 0.05334520542815162],
    [11.756672107702531, 0.05503686201894034],
    [11.968877954628164, 0.05648689321051027],
    [12.181083801553797, 0.05770046337640511],
    [12.39328964847943, 0.058688452324586145],
    [12.605495495405062, 0.05946633442976331],
    [12.817701342330695, 0.060052915174411504],
    [13.029907189256328, 0.06046902043038344],
    [13.24211303618196, 0.06073622863381866],
    [13.454318883107593, 0.06087572373454022],
    [13.666524730033226, 0.06090732932056684],
    [13.878730576958858, 0.06084876385271901],
    [14.090936423884491, 0.06071513576236451],
    [14.303142270810124, 0.060518677311482666],
    [14.515348117735757, 0.060268699204840376],
    [14.72755396466139, 0.05997173504592655],
    [14.939759811587022, 0.05963183632681613],
    [15.151965658512655, 0.05925097468752065],
    [15.364171505438287, 0.0588295081932167],
    [15.57637735236392, 0.058366671589141286],
    [15.788583199289553, 0.05786105599085673],
    [16.000789046215186, 0.05731105033010336],
    [16.212994893140817, 0.05671522427348908],
    [16.42520074006645, 0.056072639590130355],
    [16.637406586992086, 0.055383083575968405],
    [16.849612433917716, 0.054647223838530504],
    [17.061818280843347, 0.05386668835191171],
    [17.274024127768982, 0.05304407817114493],
    [17.486229974694616, 0.052182922591056106],
    [17.698435821620247, 0.051287587937177435],
    [17.91064166854588, 0.05036315169999609],
    [18.122847515471513, 0.04941525349395439],
    [18.335053362397147, 0.0484499334710198],
    [18.547259209322778, 0.04747346748242453],
    [18.75946505624841, 0.04649220660622706],
    [18.971670903174044, 0.045512426793607424],
    [19.183876750099678, 0.044540192487466206],
    [19.39608259702531, 0.04358123628210661],
    [19.60828844395094, 0.042640855156476185],
    [19.820494290876574, 0.04172382263245847],
    [20.03270013780221, 0.0408343154535329],
    [20.24490598472784, 0.039975853072085656],
    [20.45711183165347, 0.039151248352409435],
    [20.669317678579105, 0.03836256837275425],
    [20.88152352550474, 0.03761110493785334],
    [21.09372937243037, 0.03689735526250886],
    [21.305935219356, 0.03622101411599806],
    [21.518141066281636, 0.035580979390450435],
    [21.73034691320727, 0.03497537345830088],
    [21.9425527601329, 0.03440158273069523],
    [22.154758607058533, 0.033856317477247475],
    [22.366964453984167, 0.033335693219013605],
    [22.5791703009098, 0.032835333906479074],
    [22.791376147835432, 0.03235049572734687],
    [23.003581994761063, 0.031876208870940274],
    [23.215787841686698, 0.03140743304387125],
    [23.427993688612332, 0.030939221129209223],
    [23.640199535537963, 0.030466884248495578],
    [23.852405382463594, 0.029986150744001797],
    [24.06461122938923, 0.029493311340046232],
    [24.276817076314863, 0.028985343021902713],
    [24.489022923240494, 0.028460004999057757],
    [24.701228770166125, 0.02791590146167587],
    [24.91343461709176, 0.027352507614365643],
    [25.125640464017394, 0.02677015756191267],
    [25.337846310943025, 0.026169994878200823],
    [25.550052157868656, 0.02555388894579841],
    [25.76225800479429, 0.024924322238379384],
    [25.974463851719925, 0.024284255469841164],
    [26.186669698645556, 0.023636978814908255],
    [26.398875545571187, 0.02298595811332371],
    [26.61108139249682, 0.022334685045256226],
    [26.823287239422456, 0.021686539700567272],
    [27.035493086348087, 0.021044672802088257],
    [27.24769893327372, 0.020411913173963177],
    [27.459904780199352, 0.019790704003713182],
    [27.672110627124983, 0.01918306919544619],
    [27.884316474050618, 0.018590608834152547],
    [28.096522320976252, 0.018014520663407644],
    [28.308728167901883, 0.01745564269394433],
    [28.520934014827514, 0.016914510753443905],
    [28.73313986175315, 0.016391424061556855],
    [28.945345708678783, 0.015886511819768523],
    [29.157551555604414, 0.01539979433659547],
    [29.369757402530045, 0.014931233299983398],
    [29.58196324945568, 0.014480767343298347],
    [29.794169096381314, 0.014048330870107277],
    [30.006374943306945, 0.013633856021602348],
    [30.218580790232576, 0.013237259498139246],
    [30.43078663715821, 0.012858417505826504],
    [30.642992484083845, 0.012497133245912471],
    [30.855198331009476, 0.012153102002183737],
    [31.067404177935106, 0.011825878970407199],
    [31.27961002486074, 0.011514854534233777],
    [31.491815871786375, 0.011219240799198862],
    [31.704021718712006, 0.01093807196878015],
    [31.916227565637637, 0.010670219729138088],
    [32.12843341256327, 0.010414423355078587],
    [32.34063925948891, 0.010169332901122994],
    [32.55284510641454, 0.009933562713909326],
    [32.765050953340165, 0.009705751674037935],
    [32.977256800265806, 0.00948462608406498],
    [33.189462647191434, 0.00926906096256309],
    [33.40166849411707, 0.00905813564832781],
    [33.6138743410427, 0.00885118000973748],
    [33.82608018796833, 0.008647808129801017],
    [34.03828603489397, 0.008447937038632385],
    [34.2504918818196, 0.008251788844429678],
    [34.462697728745226, 0.008059875439102233],
    [34.67490357567087, 0.007872965807161444],
    [34.887109422596495, 0.007692036837150572],
    [35.09931526952213, 0.007518209416007668],
    [35.311521116447764, 0.007352672463827223],
    [35.52372696337339, 0.00719659841148732],
    [35.73593281029903, 0.00705105439129628],
    [35.94813865722466, 0.006916914039393637],
    [36.16034450415029, 0.006794775224968901],
    [36.37255035107593, 0.006684889150121577],
    [36.58475619800156, 0.006587106039698126],
    [36.79696204492719, 0.006500842019201877],
    [37.009167891852826, 0.006425070750745372],
    [37.22137373877845, 0.0063583419929925744],
    [37.433579585704095, 0.006298827546181459],
    [37.64578543262972, 0.006244393153813119],
    [37.85799127955535, 0.006192693007035614],
    [38.07019712648099, 0.006141281703333337],
    [38.28240297340662, 0.006087737016313513],
    [38.49460882033225, 0.0060297857891188356],
    [38.70681466725789, 0.005965424785823567],
    [38.919020514183515, 0.0058930284886434614],
    [39.13122636110916, 0.005811436619932177],
    [39.343432208034784, 0.005720015540593309],
    [39.55563805496041, 0.005618689516607881],
    [39.76784390188605, 0.0055079399921837465],
    [39.98004974881168, 0.005388773271119047],
    [40.192255595737315, 0.005262659187676286],
    [40.40446144266295, 0.005131445257348658],
    [40.61666728958858, 0.004997252281644281],
    [40.82887313651422, 0.004862358333146622],
    [41.041078983439846, 0.004729078419612142],
    [41.25328483036547, 0.0045996469313653735],
    [41.465490677291115, 0.004476109282914767],
    [41.67769652421674, 0.0043602280793686825],
    [41.88990237114238, 0.00425340780965936],
    [42.10210821806801, 0.004156640638327674],
    [42.31431406499364, 0.004070474471307819],
    [42.52651991191928, 0.003995003217747908],
    [42.73872575884491, 0.003929878131359524],
    [42.950931605770535, 0.003874338322051926],
    [43.163137452696176, 0.003827257974389173],
    [43.375343299621804, 0.003787207456731018],
    [43.587549146547445, 0.003752525299620503],
    [43.79975499347307, 0.0037213979068220914],
    [44.0119608403987, 0.003691943790130628],
    [44.22416668732434, 0.0036622990621738762],
    [44.43637253424997, 0.003630700877039928],
    [44.6485783811756, 0.0035955654976951973],
    [44.86078422810124, 0.003555557730770768],
    [45.072990075026865, 0.0035096486504320483],
    [45.28519592195251, 0.0034571588770390947],
    [45.497401768878134, 0.003397785211358029],
    [45.70960761580376, 0.0033316091553323235],
    [45.9218134627294, 0.0032590867511754426],
    [46.13401930965503, 0.003181020188537084],
    [46.34622515658066, 0.0030985126881105393],
    [46.5584310035063, 0.0030129091786425598],
    [46.77063685043193, 0.002925726149989995],
    [46.98284269735757, 0.0028385747048943776],
    [47.195048544283196, 0.002753081184896051],
    [47.407254391208824, 0.0026708097787733432],
    [47.619460238134465, 0.0025931912355611005],
    [47.83166608506009, 0.0025214612314958407],
    [48.04387193198572, 0.002456611141769168],
    [48.25607777891136, 0.0023993530240695657],
    [48.46828362583699, 0.0023500996216082384],
    [48.68048947276263, 0.002308959227927717],
    [48.89269531968826, 0.002275744403065582],
    [49.104901166613885, 0.0022499928511449997],
    [49.31710701353953, 0.002230998300457629],
    [49.529312860465154, 0.0022178489805870626],
    [49.74151870739078, 0.0022094712548292594],
    [49.95372455431642, 0.002204676107255677],
    [50.16593040124205, 0.002202206454530304],
    [50.37813624816769, 0.002200783597456965],
    [50.59034209509332, 0.002199151490189509],
    [50.80254794201895, 0.002196117836673976],
    [51.01475378894459, 0.002190591287246264],
    [51.226959635870216, 0.00218161418291589],
    [51.43916548279584, 0.002168390378519805],
    [51.651371329721485, 0.002150307683976825],
    [51.86357717664711, 0.0021269544251218465],
    [52.075783023572754, 0.0020981295811990535],
    [52.28798887049838, 0.0020638459470771896],
    [52.50019471742401, 0.0020243258325740258],
    [52.71240056434965, 0.001979988976346124],
    [52.92460641127528, 0.0019314326294041276],
    [53.136812258200905, 0.0018794041467314739]
  ];
  const fiber_plot = [
    [13.56140833856554, 0.02563391613901713],
    [13.652315530201648, 0.02794544080734505],
    [13.743222721837755, 0.030475625242560624],
    [13.834129913473863, 0.03322741798726903],
    [13.92503710510997, 0.03620018962109765],
    [14.015944296746078, 0.03938924860840717],
    [14.106851488382185, 0.04278542474759573],
    [14.197758680018293, 0.046374754787500104],
    [14.2886658716544, 0.05013830424928514],
    [14.379573063290508, 0.05405215620101682],
    [14.470480254926615, 0.05808759158232646],
    [14.561387446562723, 0.062211476825636415],
    [14.65229463819883, 0.06638686339045628],
    [14.743201829834938, 0.07057379109121688],
    [14.834109021471045, 0.07473027364049306],
    [14.925016213107153, 0.07881343167573525],
    [15.01592340474326, 0.0827807267735477],
    [15.106830596379368, 0.0865912406235924],
    [15.197737788015475, 0.09020693753151432],
    [15.288644979651583, 0.09359384640494602],
    [15.37955217128769, 0.09672310069160461],
    [15.470459362923798, 0.0995717813658556],
    [15.561366554559905, 0.10212351860859296],
    [15.652273746196013, 0.1043688215576308],
    [15.74318093783212, 0.10630512140064304],
    [15.834088129468228, 0.10793652992793688],
    [15.924995321104335, 0.10927333216861164],
    [16.015902512740443, 0.11033124665560957],
    [16.106809704376552, 0.11113049911843152],
    [16.197716896012658, 0.11169476416102019],
    [16.288624087648763, 0.11205003424886154],
    [16.379531279284873, 0.11222347596747956],
    [16.470438470920982, 0.1122423302438466],
    [16.561345662557088, 0.11213290657650338],
    [16.652252854193193, 0.11191971207459678],
    [16.743160045829303, 0.11162474518454489],
    [16.834067237465412, 0.1112669723566172],
    [16.924974429101518, 0.11086199449204827],
    [17.015881620737623, 0.11042189959917993],
    [17.106788812373733, 0.10995528926390541],
    [17.197696004009842, 0.10946745966654],
    [17.288603195645948, 0.10896071308466819],
    [17.379510387282053, 0.1084347730325959],
    [17.470417578918163, 0.10788727516343027],
    [17.561324770554272, 0.10731430645556668],
    [17.652231962190378, 0.10671096663454553],
    [17.743139153826483, 0.10607192787164531],
    [17.834046345462593, 0.10539197124042957],
    [17.924953537098702, 0.10466648097975985],
    [18.015860728734808, 0.10389188018561661],
    [18.106767920370913, 0.10306599410734658],
    [18.197675112007023, 0.10218832980244091],
    [18.288582303643132, 0.10126026359499347],
    [18.379489495279238, 0.10028513068048638],
    [18.470396686915343, 0.09926821439143967],
    [18.561303878551453, 0.09821663610055281],
    [18.652211070187562, 0.09713915043861412],
    [18.743118261823668, 0.09604585432216006],
    [18.834025453459773, 0.09494782203740684],
    [18.924932645095883, 0.09385668208744813],
    [19.015839836731992, 0.09278415443746944],
    [19.106747028368098, 0.09174156895879645],
    [19.197654220004203, 0.09073938708703007],
    [19.288561411640313, 0.08978674884449908],
    [19.379468603276422, 0.0888910663811195],
    [19.470375794912528, 0.0880576830912337],
    [19.561282986548633, 0.08728961427680205],
    [19.652190178184743, 0.08658738142701429],
    [19.743097369820852, 0.08594894769966864],
    [19.834004561456958, 0.08536975737980869],
    [19.924911753093063, 0.08484287722512526],
    [20.015818944729173, 0.08435923294476805],
    [20.106726136365282, 0.0839079298317623],
    [20.197633328001388, 0.08347664297469085],
    [20.288540519637493, 0.08305205966196272],
    [20.379447711273603, 0.0826203546638749],
    [20.470354902909712, 0.08216767808747208],
    [20.561262094545818, 0.08168063545482919],
    [20.652169286181923, 0.08114674052285023],
    [20.743076477818033, 0.08055482307035283],
    [20.833983669454142, 0.07989537632173964],
    [20.924890861090248, 0.07916083172367283],
    [21.015798052726353, 0.07834575228699955],
    [21.106705244362463, 0.07744693947958455],
    [21.197612435998572, 0.07646345252595911],
    [21.288519627634678, 0.07539654275527888],
    [21.379426819270783, 0.07424950916637046],
    [21.470334010906893, 0.07302748449133048],
    [21.561241202543002, 0.07173716360749123],
    [21.652148394179108, 0.07038648807535117],
    [21.743055585815213, 0.0689843018095868],
    [21.833962777451323, 0.06753999340470612],
    [21.924869969087432, 0.06606314045754659],
    [22.015777160723538, 0.06456317041157592],
    [22.106684352359643, 0.06304905107427913],
    [22.197591543995753, 0.06152902212805434],
    [22.288498735631862, 0.060010376773513674],
    [22.379405927267968, 0.05849930021826578],
    [22.470313118904073, 0.05700076915331739],
    [22.561220310540183, 0.055518513732802886],
    [22.652127502176292, 0.054055040969578305],
    [22.743034693812398, 0.05261171595015261],
    [22.833941885448503, 0.051188894924003005],
    [22.924849077084613, 0.04978610220080938],
    [23.015756268720722, 0.04840224096420092],
    [23.106663460356827, 0.04703582665596469],
    [23.197570651992933, 0.04568523057629163],
    [23.288477843629042, 0.04434892085682557],
    [23.37938503526515, 0.04302568805625508],
    [23.470292226901257, 0.041714843344938704],
    [23.561199418537363, 0.04041637859700558],
    [23.652106610173472, 0.039131079668105424],
    [23.74301380180958, 0.037860586632579754],
    [23.833920993445687, 0.03660739766823246],
    [23.924828185081793, 0.03537481645144954],
    [24.015735376717902, 0.03416684616996252],
    [24.10664256835401, 0.03298803636651889],
    [24.197549759990117, 0.03184329158474961],
    [24.288456951626223, 0.030737653006445185],
    [24.379364143262332, 0.029676065790033027],
    [24.47027133489844, 0.028663145535759438],
    [24.561178526534547, 0.027702957166367743],
    [24.652085718170653, 0.02679881853997734],
    [24.742992909806762, 0.025953139384465956],
    [24.83390010144287, 0.02516730379548343],
    [24.924807293078977, 0.024441601751706715],
    [25.015714484715083, 0.023775212076178567],
    [25.106621676351192, 0.023166236225356397],
    [25.1975288679873, 0.022611779422349307],
    [25.288436059623407, 0.02210807314703663],
    [25.379343251259513, 0.02165063099467745],
    [25.470250442895622, 0.021234428511389066],
    [25.56115763453173, 0.020854096854961444],
    [25.652064826167837, 0.020504120009954677],
    [25.742972017803943, 0.02017902576088191],
    [25.833879209440052, 0.019873561615938652],
    [25.92478640107616, 0.019582848271288034],
    [26.015693592712267, 0.019302504894097846],
    [26.106600784348373, 0.019028742359890397],
    [26.197507975984482, 0.018758422490366623],
    [26.28841516762059, 0.018489083197675324],
    [26.379322359256697, 0.018218931162010295],
    [26.470229550892803, 0.01794680518089819],
    [26.561136742528912, 0.01767211457745929],
    [26.65204393416502, 0.01739475800409489],
    [26.742951125801127, 0.017115028604337022],
    [26.833858317437233, 0.01683351178808143],
    [26.924765509073342, 0.01655098183435113],
    [27.01567270070945, 0.016268303172196897],
    [27.106579892345557, 0.015986341526903453],
    [27.197487083981663, 0.01570588919045085],
    [27.288394275617772, 0.015427607530891644],
    [27.37930146725388, 0.015151988557665797],
    [27.470208658889987, 0.014879335984692812],
    [27.561115850526093, 0.01460976486664601],
    [27.652023042162202, 0.014343217618299124],
    [27.74293023379831, 0.014079493153940968],
    [27.833837425434417, 0.013818285087039993],
    [27.924744617070523, 0.01355922447658806],
    [28.015651808706632, 0.01330192253846739],
    [28.10655900034274, 0.01304600906968251],
    [28.197466191978847, 0.012791163037766846],
    [28.288373383614953, 0.012537132809394667],
    [28.379280575251062, 0.012283744741559813],
    [28.47018776688717, 0.012030900220824146],
    [28.561094958523277, 0.011778562581150943],
    [28.652002150159383, 0.01152673652587072],
    [28.742909341795492, 0.01127544360183756],
    [28.8338165334316, 0.011024697824424138],
    [28.924723725067707, 0.010774485665292544],
    [29.015630916703813, 0.010524754267266402],
    [29.106538108339922, 0.01027541096392569],
    [29.19744529997603, 0.01002633602195596],
    [29.288352491612137, 0.009777409096832628],
    [29.379259683248243, 0.009528548331266295],
    [29.470166874884352, 0.009279759481503759],
    [29.56107406652046, 0.009031191081425598],
    [29.651981258156567, 0.008783190587800416],
    [29.742888449792673, 0.008536355804920787],
    [29.833795641428786, 0.008291575738609506],
    [29.92470283306489, 0.00805005540903217],
    [30.015610024700997, 0.007813320042119895],
    [30.106517216337103, 0.007583195398027845],
    [30.197424407973216, 0.007361762679258434],
    [30.28833159960932, 0.007151288357679147],
    [30.379238791245427, 0.006954131216970441],
    [30.470145982881533, 0.006772630768232769],
    [30.561053174517646, 0.006608982813003536],
    [30.65196036615375, 0.006465109171721169],
    [30.742867557789857, 0.006342529369158102],
    [30.833774749425963, 0.006242242311480203],
    [30.924681941062076, 0.006164625683450066],
    [31.01558913269818, 0.0061093599611508585],
    [31.106496324334287, 0.00607538263571643],
    [31.197403515970393, 0.0060608765691279745],
    [31.288310707606506, 0.006063294470218648],
    [31.37921789924261, 0.006079419418060807],
    [31.470125090878717, 0.00610545930584235],
    [31.561032282514823, 0.006137171161054111],
    [31.651939474150936, 0.006170009633566577]
  ];
  const fruits_plot = [
    [0.420956, 1.5188207984638533],
    [0.4323307463316583, 1.602371715010029],
    [0.4437054926633166, 1.6606229977670828],
    [0.4550802389949749, 1.69309938826333],
    [0.4664549853266332, 1.7010297392296565],
    [0.4778297316582915, 1.6870549934943058],
    [0.4892044779899498, 1.6548156018125328],
    [0.5005792243216081, 1.6084954197475159],
    [0.5119539706532664, 1.5523922568141257],
    [0.5233287169849248, 1.490565261556385],
    [0.534703463316583, 1.4265836739946858],
    [0.5460782096482413, 1.3633777809636756],
    [0.5574529559798996, 1.3031766165579246],
    [0.568827702311558, 1.2475100933509466],
    [0.5802024486432162, 1.1972545458535873],
    [0.5915771949748745, 1.1527066663328094],
    [0.6029519413065328, 1.1136775198830091],
    [0.6143266876381912, 1.0796026524966078],
    [0.6257014339698495, 1.0496649137697807],
    [0.6370761803015077, 1.0229240058992228],
    [0.6484509266331661, 0.9984427558873697],
    [0.6598256729648244, 0.9753969587794518],
    [0.6712004192964827, 0.9531552252572014],
    [0.6825751656281409, 0.9313184447934786],
    [0.6939499119597993, 0.9097148662340613],
    [0.7053246582914576, 0.888354951564423],
    [0.7166994046231159, 0.8673580251165178],
    [0.7280741509547741, 0.8468682801899837],
    [0.7394488972864325, 0.8269794658705022],
    [0.7508236436180908, 0.8076850780772347],
    [0.7621983899497491, 0.788864711670086],
    [0.7735731362814073, 0.7703088663050945],
    [0.7849478826130657, 0.7517758796707328],
    [0.796322628944724, 0.7330676981323744],
    [0.8076973752763823, 0.7141073094865036],
    [0.8190721216080405, 0.6950004933485406],
    [0.8304468679396989, 0.6760678649940874],
    [0.8418216142713573, 0.6578390443618385],
    [0.8531963606030155, 0.6410077918352585],
    [0.8645711069346738, 0.6263536778352773],
    [0.8759458532663321, 0.614641136675492],
    [0.8873205995979905, 0.6065099349599488],
    [0.8986953459296487, 0.6023720366884621],
    [0.910070092261307, 0.6023288592347544],
    [0.9214448385929653, 0.6061204621920345],
    [0.9328195849246236, 0.6131147358714113],
    [0.9441943312562819, 0.622340443117915],
    [0.9555690775879403, 0.6325631744645926],
    [0.9669438239195985, 0.6423980841294566],
    [0.9783185702512568, 0.6504480714598418],
    [0.9896933165829152, 0.6554515505642424],
    [1.0010680629145736, 0.6564210317559124],
    [1.0124428092462319, 0.6527533311460995],
    [1.0238175555778901, 0.6442948984024754],
    [1.0351923019095484, 0.6313514612930979],
    [1.0465670482412066, 0.6146391677280941],
    [1.0579417945728649, 0.5951833113024809],
    [1.069316540904523, 0.5741789273975276],
    [1.0806912872361816, 0.5528335246060235],
    [1.09206603356784, 0.5322148925922245],
    [1.1034407798994983, 0.5131258589591039],
    [1.1148155262311565, 0.49602327959894876],
    [1.1261902725628148, 0.4809912447837616],
    [1.137565018894473, 0.46776969574924687],
    [1.1489397652261313, 0.45583083940892705],
    [1.1603145115577895, 0.4444884166473364],
    [1.171689257889448, 0.43302031125676727],
    [1.1830640042211065, 0.42078401545975663],
    [1.1944387505527647, 0.4073072554034928],
    [1.205813496884423, 0.39234200912701167],
    [1.2171882432160812, 0.37587790172284885],
    [1.2285629895477395, 0.3581187987167529],
    [1.2399377358793977, 0.3394326106961393],
    [1.251312482211056, 0.32028760998313843],
    [1.2626872285427144, 0.3011884880839299],
    [1.274061974874373, 0.2826223984985812],
    [1.2854367212060311, 0.26502051152903117],
    [1.2968114675376894, 0.24873567947285224],
    [1.3081862138693476, 0.2340330721506742],
    [1.3195609602010059, 0.22108896905104256],
    [1.3309357065326641, 0.20999341898809376],
    [1.3423104528643224, 0.20075461412644793],
    [1.3536851991959808, 0.19330550028829593],
    [1.3650599455276393, 0.1875151685982165],
    [1.3764346918592976, 0.18320802077449644],
    [1.3878094381909558, 0.18019221054033427],
    [1.399184184522614, 0.17829576379345868],
    [1.4105589308542723, 0.1774050074945119],
    [1.4219336771859306, 0.17749677659117113],
    [1.4333084235175888, 0.1786545742832272],
    [1.444683169849247, 0.1810602659934376],
    [1.4560579161809057, 0.18495710631872248],
    [1.467432662512564, 0.1905862184739445],
    [1.4788074088442222, 0.19810564674341039],
    [1.4901821551758805, 0.20750698601248863],
    [1.5015569015075387, 0.21854764739745994],
    [1.512931647839197, 0.2307159018396904],
    [1.5243063941708552, 0.24324074400820536],
    [1.5356811405025135, 0.25515020005731476],
    [1.5470558868341722, 0.2653717574618688],
    [1.5584306331658304, 0.27285943313564964],
    [1.5698053794974887, 0.27672585661706983],
    [1.581180125829147, 0.2763561901499766],
    [1.5925548721608052, 0.2714841733449101],
    [1.6039296184924634, 0.2622182333633561],
    [1.6153043648241217, 0.24901555778988788],
    [1.62667911115578, 0.23261187679585324],
    [1.6380538574874386, 0.2139221813594529],
    [1.6494286038190968, 0.19393120732989735],
    [1.660803350150755, 0.17359184127869162],
    [1.6721780964824133, 0.15374536415970666],
    [1.6835528428140716, 0.13507114182699126],
    [1.6949275891457298, 0.11806677105301017],
    [1.706302335477388, 0.10305432244247602],
    [1.7176770818090463, 0.09020510035298825],
    [1.729051828140705, 0.0795744394911382],
    [1.7404265744723633, 0.07113902781360225],
    [1.7518013208040215, 0.0648312787803798],
    [1.7631760671356798, 0.06056752137297023],
    [1.774550813467338, 0.058268591293900975],
    [1.7859255597989963, 0.05787249342487358],
    [1.7973003061306545, 0.05933921475778355],
    [1.8086750524623127, 0.06264779852582167],
    [1.8200497987939714, 0.0677858365412569],
    [1.8314245451256297, 0.07473192089007291],
    [1.842799291457288, 0.08343244972642452],
    [1.8541740377889462, 0.0937753933838018],
    [1.8655487841206044, 0.10556487305697822],
    [1.8769235304522627, 0.11850125465034879],
    [1.888298276783921, 0.13217152421675452],
    [1.8996730231155792, 0.1460537779351097],
    [1.9110477694472379, 0.159537784207617],
    [1.9224225157788961, 0.1719610797951107],
    [1.9337972621105544, 0.18265744648124213],
    [1.9451720084422126, 0.19101241632699928],
    [1.9565467547738709, 0.1965190991616472],
    [1.967921501105529, 0.1988273340324155],
    [1.9792962474371874, 0.19777992295451552],
    [1.9906709937688456, 0.1934313102677762],
    [2.0020457401005043, 0.18604622269979496],
    [2.0134204864321625, 0.17607816463404546],
    [2.024795232763821, 0.16412998612118798],
    [2.036169979095479, 0.15090077413014324],
    [2.0475447254271373, 0.13712486719384004],
    [2.0589194717587955, 0.12350969544435843],
    [2.0702942180904538, 0.11067927076609985],
    [2.081668964422112, 0.0991294234668248],
    [2.0930437107537707, 0.08919932849647413],
    [2.104418457085429, 0.08106164307476536],
    [2.115793203417087, 0.07473098591515961],
    [2.1271679497487455, 0.0700879348681688],
    [2.1385426960804037, 0.06691365701128747],
    [2.149917442412062, 0.06492911178066144],
    [2.16129218874372, 0.06383272594852035],
    [2.1726669350753784, 0.0633315358206269],
    [2.184041681407037, 0.06316277220940908],
    [2.1954164277386954, 0.06310525584838111],
    [2.2067911740703536, 0.06298219777364933],
    [2.218165920402012, 0.06265852934431744],
    [2.22954066673367, 0.06203638105370152],
    [2.2409154130653284, 0.0610517373049727],
    [2.2522901593969866, 0.05967386554138135],
    [2.263664905728645, 0.05790732636160502],
    [2.2750396520603036, 0.0557947738344751],
    [2.286414398391962, 0.05341783244293279],
    [2.29778914472362, 0.050893360630262244],
    [2.3091638910552783, 0.048363377662231775],
    [2.3205386373869366, 0.0459785750366327],
    [2.331913383718595, 0.04387720895807414],
    [2.343288130050253, 0.04216276919030316],
    [2.3546628763819113, 0.04088470283941541],
    [2.36603762271357, 0.040026374212381435],
    [2.3774123690452282, 0.03950333511583017],
    [2.3887871153768865, 0.03917308010733396],
    [2.4001618617085447, 0.03885518342185512],
    [2.411536608040203, 0.03835857939032754],
    [2.4229113543718612, 0.03751125977957533],
    [2.4342861007035195, 0.03618717993345135],
    [2.4456608470351777, 0.034325810593497215],
    [2.457035593366836, 0.03194138406178887],
    [2.468410339698494, 0.029121058745445526],
    [2.479785086030153, 0.02601342416554515],
    [2.491159832361811, 0.02281045753815831],
    [2.5025345786934694, 0.01972684519466547],
    [2.5139093250251277, 0.01698037155512169],
    [2.525284071356786, 0.014776004265747083],
    [2.536658817688444, 0.01329473490075131],
    [2.5480335640201024, 0.012686644872683337],
    [2.559408310351761, 0.013066502794350534],
    [2.5707830566834193, 0.014509765764958644],
    [2.5821578030150776, 0.01704724255381625],
    [2.593532549346736, 0.020657745270556978],
    [2.604907295678394, 0.02525948804251008],
    [2.6162820420100523, 0.030702365399534665],
    [2.6276567883417106, 0.03676414045578068],
    [2.639031534673369, 0.04315367984889729],
    [2.650406281005027, 0.04952356282380732],
    [2.6617810273366858, 0.05549277060767705],
    [2.673155773668344, 0.06067805467094184],
    [2.6845305200000023, 0.06473046809553436]
  ];
  const dairy_plot = [
    [0.946226, 0.3975038475612867],
    [0.9592564509872771, 0.40537682217839016],
    [0.9722869019745541, 0.41313366469536034],
    [0.9853173529618312, 0.42076516419259474],
    [0.9983478039491083, 0.42826240069451305],
    [1.0113782549363854, 0.4356167680527656],
    [1.0244087059236624, 0.44281999510958486],
    [1.0374391569109394, 0.44986416505004245],
    [1.0504696078982165, 0.45674173286601777],
    [1.0635000588854937, 0.46344554086806633],
    [1.0765305098727707, 0.46996883219675906],
    [1.0895609608600476, 0.47630526229906117],
    [1.1025914118473248, 0.4824489083506408],
    [1.115621862834602, 0.48839427661895723],
    [1.128652313821879, 0.49413630777726925],
    [1.1416827648091559, 0.49967038019311866],
    [1.154713215796433, 0.5049923112287674],
    [1.1677436667837102, 0.510098356604415],
    [1.1807741177709872, 0.5149852078869819],
    [1.1938045687582641, 0.5196499881791468],
    [1.2068350197455413, 0.5240902460941612],
    [1.2198654707328185, 0.5283039481116608],
    [1.2328959217200954, 0.532289469418598],
    [1.2459263727073724, 0.5360455833472383],
    [1.2589568236946496, 0.5395714495285008],
    [1.2719872746819267, 0.5428666008852265],
    [1.2850177256692037, 0.5459309295931],
    [1.2980481766564806, 0.5487646721413755],
    [1.3110786276437578, 0.5513683936266439],
    [1.324109078631035, 0.5537429714141454],
    [1.337139529618312, 0.555889578300751],
    [1.350169980605589, 0.5578096653123313],
    [1.363200431592866, 0.5595049442656747],
    [1.3762308825801433, 0.560977370221932],
    [1.3892613335674202, 0.5622291239535903],
    [1.4022917845546972, 0.5632625945425156],
    [1.4153222355419743, 0.5640803622197049],
    [1.4283526865292515, 0.5646851815510978],
    [1.4413831375165285, 0.565079965066002],
    [1.4544135885038054, 0.5652677674169878],
    [1.4674440394910826, 0.5652517701513045],
    [1.4804744904783598, 0.5650352671653093],
    [1.4935049414656367, 0.5646216509042642],
    [1.5065353924529137, 0.5640143993598841],
    [1.5195658434401909, 0.563217063909889],
    [1.532596294427468, 0.5622332580329825],
    [1.545626745414745, 0.5610666469242949],
    [1.558657196402022, 0.5597209380266551],
    [1.5716876473892991, 0.5581998724843599],
    [1.5847180983765763, 0.5565072175169313],
    [1.5977485493638532, 0.5546467597028354],
    [1.6107790003511302, 0.552622299154448],
    [1.6238094513384074, 0.5504376445589108],
    [1.6368399023256845, 0.5480966090518115],
    [1.6498703533129615, 0.5456030068853214],
    [1.6629008043002385, 0.5429606508460247],
    [1.6759312552875156, 0.540173350372788],
    [1.6889617062747928, 0.5372449103209658],
    [1.7019921572620698, 0.5341791303152682],
    [1.7150226082493467, 0.5309798046306291],
    [1.728053059236624, 0.5276507225381624],
    [1.741083510223901, 0.524195669051715],
    [1.754113961211178, 0.5206184260094537],
    [1.767144412198455, 0.5169227734246231],
    [1.7801748631857321, 0.5131124910400329],
    [1.7932053141730093, 0.5091913600217464],
    [1.8062357651602863, 0.5051631647288394],
    [1.8192662161475632, 0.5010316944983373],
    [1.8322966671348404, 0.4968007453868767],
    [1.8453271181221176, 0.4924741218137926],
    [1.8583575691093945, 0.4880556380537202],
    [1.8713880200966715, 0.48354911953042207],
    [1.8844184710839487, 0.4789584038683814],
    [1.8974489220712258, 0.4742873416621938],
    [1.9104793730585028, 0.46953979692943415],
    [1.9235098240457797, 0.4647196472166155],
    [1.936540275033057, 0.45983078333379823],
    [1.9495707260203339, 0.4548771086974856],
    [1.962601177007611, 0.44986253826749073],
    [1.9756316279948882, 0.4447909970677004],
    [1.9886620789821652, 0.43966641828651426],
    [2.001692529969442, 0.4344927409569088],
    [2.0147229809567193, 0.42927390722155195],
    [2.0277534319439967, 0.4240138591923667],
    [2.040783882931273, 0.4187165354189566],
    [2.0538143339185506, 0.41338586698405966],
    [2.0668447849058276, 0.40802577324816824],
    [2.0798752358931045, 0.4026401572690758],
    [2.092905686880382, 0.39723290092541363],
    [2.1059361378676584, 0.39180785977592547],
    [2.118966588854936, 0.386368857689023],
    [2.1319970398422132, 0.38091968127934633],
    [2.1450274908294897, 0.3754640741895107],
    [2.158057941816767, 0.3700057312569473],
    [2.171088392804044, 0.36454829260681915],
    [2.184118843791321, 0.3590953377123559],
    [2.1971492947785984, 0.3536503794643169],
    [2.210179745765875, 0.34821685829128984],
    [2.2232101967531523, 0.3427981363719326],
    [2.2362406477404297, 0.33739749197945934],
    [2.2492710987277063, 0.3320181139975318],
    [2.2623015497149837, 0.3266630966452105],
    [2.2753320007022606, 0.32133543444690355],
    [2.2883624516895376, 0.3160380174812131],
    [2.301392902676815, 0.3107736269402083],
    [2.3144233536640915, 0.3055449310282815],
    [2.327453804651369, 0.3003544812269143],
    [2.3404842556386463, 0.29520470894896883],
    [2.3535147066259228, 0.29009792260286815],
    [2.3665451576132, 0.28503630508429484],
    [2.379575608600477, 0.2800219117093431],
    [2.392606059587754, 0.2750566686003037],
    [2.4056365105750315, 0.2701423715316028],
    [2.418666961562308, 0.2652806852403599],
    [2.4316974125495854, 0.26047314320266274],
    [2.4447278635368628, 0.2557211478734593],
    [2.4577583145241393, 0.2510259713850335],
    [2.4707887655114167, 0.2463887566956157],
    [2.4838192164986936, 0.24181051917725843],
    [2.4968496674859706, 0.2372921486290138],
    [2.509880118473248, 0.23283441169893057],
    [2.5229105694605245, 0.2284379546960691],
    [2.535941020447802, 0.22410330677142115],
    [2.5489714714350793, 0.21983088344462312],
    [2.562001922422356, 0.2156209904515288],
    [2.575032373409633, 0.21147382788624028],
    [2.58806282439691, 0.20738949460958256],
    [2.601093275384187, 0.20336799289533947],
    [2.6141237263714645, 0.19940923328414192],
    [2.627154177358741, 0.1955130396147793],
    [2.6401846283460184, 0.19167915420201526],
    [2.653215079333296, 0.18790724312982018],
    [2.6662455303205723, 0.18419690162918873],
    [2.6792759813078497, 0.18054765950988336],
    [2.6923064322951267, 0.1769589866158228],
    [2.7053368832824036, 0.17343029827483672],
    [2.718367334269681, 0.1699609607141884],
    [2.7313977852569575, 0.1665502964143556],
    [2.744428236244235, 0.1631975893748964],
    [2.7574586872315123, 0.1599020902676309],
    [2.770489138218789, 0.1566630214537647],
    [2.7835195892060662, 0.15347958184340704],
    [2.796550040193343, 0.15035095157758072],
    [2.80958049118062, 0.14727629651474164],
    [2.8226109421678975, 0.1442547725056486],
    [2.835641393155174, 0.14128552944251632],
    [2.8486718441424514, 0.13836771507022597],
    [2.861702295129729, 0.13550047854957578],
    [2.8747327461170054, 0.13268297376442162],
    [2.8877631971042828, 0.12991436236658963],
    [2.9007936480915597, 0.12719381655445855],
    [2.9138240990788367, 0.12452052158310274],
    [2.926854550066114, 0.12189367800547479],
    [2.9398850010533906, 0.11931250364632083],
    [2.952915452040668, 0.11677623531182937],
    [2.9659459030279454, 0.11428413023990412],
    [2.978976354015222, 0.11183546729731533],
    [2.9920068050024993, 0.10942954793145093],
    [3.0050372559897767, 0.10706569688562736],
    [3.018067706977053, 0.10474326268810037],
    [3.0310981579643306, 0.10246161792585798],
    [3.044128608951607, 0.10022015931533063],
    [3.0571590599388845, 0.09801830758264868],
    [3.070189510926162, 0.09585550716696156],
    [3.0832199619134384, 0.09373122576061647],
    [3.096250412900716, 0.09164495370041491],
    [3.109280863887993, 0.08959620322439277],
    [3.1223113148752697, 0.08758450760863848],
    [3.135341765862547, 0.08560942019864792],
    [3.1483722168498236, 0.08367051334946324],
    [3.161402667837101, 0.0817673772889503],
    [3.1744331188243784, 0.0798996189176338],
    [3.187463569811655, 0.07806686055863381],
    [3.2004940207989323, 0.07626873867043422],
    [3.2135244717862097, 0.07450490253472636],
    [3.226554922773486, 0.07277501293075413],
    [3.2395853737607636, 0.07107874080715094],
    [3.25261582474804, 0.06941576596128343],
    [3.2656462757353175, 0.06778577573550866],
    [3.278676726722595, 0.06618846373873068],
    [3.2917071777098714, 0.064623528601066],
    [3.304737628697149, 0.06309067276842713],
    [3.317768079684426, 0.061589601343184294],
    [3.3307985306717027, 0.06012002097600422],
    [3.34382898165898, 0.058681638813432475],
    [3.3568594326462566, 0.05727416150496687],
    [3.369889883633534, 0.055897294272421835],
    [3.3829203346208114, 0.05455074004405088],
    [3.395950785608088, 0.05323419865495682],
    [3.4089812365953653, 0.05194736611478643],
    [3.4220116875826427, 0.0506899339432505],
    [3.4350421385699192, 0.049461588573371025],
    [3.4480725895571966, 0.04826201082189596],
    [3.461103040544473, 0.04709087542613484],
    [3.4741334915317506, 0.04594785064573998],
    [3.487163942519028, 0.044832597927972284],
    [3.5001943935063045, 0.04374477163472364],
    [3.513224844493582, 0.04268401882898141],
    [3.5262552954808593, 0.041649979118842016],
    [3.5392857464681358, 0.040642284556597234]
  ];
  const whole_grains_plot = [
    [1.202341, 1.5336978107773926],
    [1.2075189556230292, 1.5557162462932737],
    [1.2126969112460584, 1.576414068383273],
    [1.2178748668690875, 1.595749768897057],
    [1.2230528224921167, 1.6136868636703976],
    [1.2282307781151458, 1.6301940454276918],
    [1.233408733738175, 1.645245303308608],
    [1.238586689361204, 1.6588200084165639],
    [1.2437646449842332, 1.6709029650999723],
    [1.2489426006072624, 1.681484427988225],
    [1.2541205562302915, 1.6905600851106846],
    [1.2592985118533206, 1.6981310077242098],
    [1.2644764674763498, 1.7042035677600456],
    [1.269654423099379, 1.7087893240701908],
    [1.274832378722408, 1.7119048789052318],
    [1.2800103343454372, 1.7135717062853697],
    [1.2851882899684663, 1.7138159541340807],
    [1.2903662455914955, 1.7126682222253433],
    [1.2955442012145246, 1.7101633181514317],
    [1.3007221568375538, 1.7063399936461332],
    [1.305900112460583, 1.7012406636972441],
    [1.311078068083612, 1.6949111109550048],
    [1.3162560237066412, 1.6874001779842696],
    [1.3214339793296703, 1.6787594499252267],
    [1.3266119349526995, 1.6690429301143719],
    [1.3317898905757286, 1.658306711181992],
    [1.3369678461987577, 1.6466086440800916],
    [1.3421458018217869, 1.634008007412439],
    [1.347323757444816, 1.6205651793349765],
    [1.3525017130678452, 1.6063413141740797],
    [1.3576796686908743, 1.5913980257734583],
    [1.3628576243139034, 1.5757970794319789],
    [1.3680355799369326, 1.5596000941342096],
    [1.3732135355599617, 1.5428682566088239],
    [1.3783914911829909, 1.5256620485768395],
    [1.38356944680602, 1.5080409883762267],
    [1.3887474024290491, 1.4900633879731826],
    [1.3939253580520783, 1.4717861261962781],
    [1.3991033136751074, 1.4532644388581881],
    [1.4042812692981366, 1.4345517262655043],
    [1.4094592249211657, 1.4156993784579928],
    [1.4146371805441948, 1.3967566183696896],
    [1.419815136167224, 1.377770362964468],
    [1.4249930917902531, 1.3587851022689101],
    [1.4301710474132823, 1.3398427961079358],
    [1.4353490030363114, 1.320982788242106],
    [1.4405269586593406, 1.302241737511175],
    [1.4457049142823697, 1.2836535655069192],
    [1.4508828699053988, 1.2652494202263769],
    [1.456060825528428, 1.247057655099393],
    [1.4612387811514571, 1.2291038227340536],
    [1.4664167367744863, 1.2114106826876743],
    [1.4715946923975154, 1.1939982225407506],
    [1.4767726480205445, 1.176883691532201],
    [1.4819506036435737, 1.160081646001849],
    [1.4871285592666028, 1.1436040058797177],
    [1.492306514889632, 1.1274601214630051],
    [1.497484470512661, 1.1116568497249955],
    [1.5026624261356902, 1.0961986394098524],
    [1.5078403817587194, 1.0810876241781209],
    [1.5130183373817485, 1.0663237230815454],
    [1.5181962930047777, 1.0519047476605519],
    [1.5233742486278068, 1.0378265149734114],
    [1.528552204250836, 1.0240829658814876],
    [1.533730159873865, 1.0106662879302293],
    [1.5389081154968942, 0.9975670421802454],
    [1.5440860711199234, 0.9847742933555231],
    [1.5492640267429525, 0.9722757426890695],
    [1.5544419823659816, 0.9600578628563534],
    [1.5596199379890108, 0.9481060343973305],
    [1.56479789361204, 0.9364046830364612],
    [1.569975849235069, 0.9249374173178531],
    [1.5751538048580982, 0.9136871659804686],
    [1.5803317604811273, 0.9026363145047822],
    [1.5855097161041565, 0.8917668402699116],
    [1.5906876717271856, 0.8810604457675111],
    [1.5958656273502148, 0.8704986893273576],
    [1.601043582973244, 0.8600631128191045],
    [1.606221538596273, 0.8497353658060106],
    [1.6113994942193022, 0.839497325639908],
    [1.6165774498423313, 0.829331213001933],
    [1.6217554054653605, 0.8192197024122283],
    [1.6269333610883896, 0.809146027252333],
    [1.6321113167114187, 0.799094078868396],
    [1.6372892723344479, 0.7890484993500945],
    [1.642467227957477, 0.778994767610782],
    [1.6476451835805062, 0.7689192784272167],
    [1.6528231392035353, 0.7588094141341651],
    [1.6580010948265644, 0.7486536087079599],
    [1.6631790504495936, 0.7384414040150407],
    [1.6683570060726227, 0.7281634980459467],
    [1.6735349616956519, 0.7178117850010587],
    [1.678712917318681, 0.7073793871426455],
    [1.6838908729417101, 0.6968606783763324],
    [1.6890688285647393, 0.6862512995755181],
    [1.6942467841877684, 0.6755481657117183],
    [1.6994247398107976, 0.6647494649041779],
    [1.7046026954338267, 0.6538546495509227],
    [1.7097806510568558, 0.6428644197515223],
    [1.714958606679885, 0.631780699278066],
    [1.7201365623029141, 0.6206066043950342],
    [1.7253145179259433, 0.6093464058701988],
    [1.7304924735489724, 0.5980054845573916],
    [1.7356704291720015, 0.58659028096727],
    [1.7408483847950307, 0.5751082392737102],
    [1.7460263404180598, 0.5635677462315553],
    [1.751204296041089, 0.5519780655048249],
    [1.756382251664118, 0.5403492679240601],
    [1.7615602072871472, 0.5286921582067428],
    [1.7667381629101764, 0.5170181986852741],
    [1.7719161185332055, 0.5053394305934361],
    [1.7770940741562347, 0.49366839346448427],
    [1.7822720297792638, 0.48201804319170405],
    [1.787449985402293, 0.4704016692962694],
    [1.792627941025322, 0.4588328119372251],
    [1.7978058966483512, 0.44732517918480497],
    [1.8029838522713804, 0.43589256506139384],
    [1.8081618078944095, 0.42454876883448145],
    [1.8133397635174386, 0.41330751602297783],
    [1.8185177191404678, 0.402182381553486],
    [1.823695674763497, 0.39118671547533396],
    [1.828873630386526, 0.38033357161434733],
    [1.8340515860095552, 0.3696356395147179],
    [1.8392295416325843, 0.35910517998645397],
    [1.8444074972556135, 0.3487539645435751],
    [1.8495854528786426, 0.33859321898472483],
    [1.8547634085016718, 0.3286335713349425],
    [1.859941364124701, 0.318885004333451],
    [1.86511931974773, 0.30935681261949804],
    [1.8702972753707592, 0.3000575647352351],
    [1.8754752309937883, 0.29099507003274544],
    [1.8806531866168175, 0.28217635054076506],
    [1.8858311422398466, 0.2736076178163809],
    [1.8910090978628757, 0.26529425477754165],
    [1.8961870534859049, 0.25724080248416786],
    [1.901365009108934, 0.24945095180859975],
    [1.9065429647319632, 0.24192753991065716],
    [1.9117209203549923, 0.2346725514082535],
    [1.9168988759780214, 0.2276871241118576],
    [1.9220768316010506, 0.22097155916968333],
    [1.9272547872240797, 0.21452533545067642],
    [1.9324327428471089, 0.20834712797406324],
    [1.937610698470138, 0.20243483017731587],
    [1.9427886540931671, 0.19678557979925457],
    [1.9479666097161963, 0.1913957881410363],
    [1.9531445653392254, 0.18626117245573195],
    [1.9583225209622546, 0.18137679120639122],
    [1.9635004765852837, 0.1767370819234411],
    [1.9686784322083128, 0.172335901384652],
    [1.973856387831342, 0.16816656783488276],
    [1.9790343434543711, 0.1642219049584614],
    [1.9842122990774003, 0.16049428731405407],
    [1.9893902547004294, 0.15697568694075986],
    [1.9945682103234585, 0.15365772084432022],
    [1.9997461659464877, 0.1505316990742944],
    [2.004924121569517, 0.14758867310642745],
    [2.010102077192546, 0.1448194842494755],
    [2.015280032815575, 0.14221481180221007],
    [2.0204579884386042, 0.13976522069428424],
    [2.0256359440616336, 0.13746120835426445],
    [2.0308138996846625, 0.135293250558669],
    [2.0359918553076914, 0.13325184602826912],
    [2.041169810930721, 0.1313275595512279],
    [2.04634776655375, 0.12951106342710705],
    [2.051525722176779, 0.12779317704156135],
    [2.056703677799808, 0.12616490439796113],
    [2.0618816334228374, 0.12461746944964769],
    [2.0670595890458667, 0.12314234909445526],
    [2.0722375446688956, 0.12173130371184784],
    [2.0774155002919246, 0.12037640514195463],
    [2.082593455914954, 0.11907006202492144],
    [2.0877714115379833, 0.11780504243840613],
    [2.092949367161012, 0.11657449379010505],
    [2.098127322784041, 0.11537195994124072],
    [2.1033052784070705, 0.11419139555541595],
    [2.1084832340301, 0.11302717768528106],
    [2.1136611896531288, 0.11187411462664759],
    [2.1188391452761577, 0.1107274520862533],
    [2.124017100899187, 0.1095828767247324],
    [2.1291950565222164, 0.10843651715088629],
    [2.1343730121452453, 0.10728494245652109],
    [2.1395509677682742, 0.10612515839320961],
    [2.1447289233913036, 0.10495460130286965],
    [2.149906879014333, 0.10377112992372096],
    [2.155084834637362, 0.10257301520083228],
    [2.160262790260391, 0.10135892823730575],
    [2.16544074588342, 0.10012792652731868],
    [2.1706187015064495, 0.09887943861612529],
    [2.1757966571294785, 0.09761324733470152],
    [2.1809746127525074, 0.09632947175824279],
    [2.1861525683755367, 0.0950285480376707],
    [2.191330523998566, 0.09371120925273554],
    [2.196508479621595, 0.09237846443318945],
    [2.201686435244624, 0.09103157689202371],
    [2.2068643908676533, 0.08967204201110186],
    [2.2120423464906827, 0.08830156461551188],
    [2.2172203021137116, 0.08692203606820363],
    [2.2223982577367405, 0.08553551121143634],
    [2.22757621335977, 0.08414418527615827],
    [2.232754168982799, 0.08275037087471425]
  ];
  const calcium_plot = [
    [842.5717915185857, 0.0011826433915530785],
    [847.2611712919595, 0.0014214950116311364],
    [851.9505510653333, 0.001656793501449504],
    [856.6399308387072, 0.001868933456951871],
    [861.329310612081, 0.002038158488032279],
    [866.0186903854549, 0.0021479467394637107],
    [870.7080701588287, 0.0021880998745176694],
    [875.3974499322026, 0.0021567314482611737],
    [880.0868297055764, 0.002060629208060719],
    [884.7762094789502, 0.0019139199850215457],
    [889.4655892523241, 0.0017354301087502688],
    [894.1549690256979, 0.0015454466534124893],
    [898.8443487990718, 0.0013626543285073032],
    [903.5337285724456, 0.0012018585895092616],
    [908.2231083458195, 0.0010727985852124592],
    [912.9124881191933, 0.0009800276324170715],
    [917.6018678925672, 0.0009235981281634464],
    [922.291247665941, 0.0009001837455720721],
    [926.9806274393148, 0.0009042981872645189],
    [931.6700072126887, 0.0009293795804129079],
    [936.3593869860625, 0.0009686415702651198],
    [941.0487667594364, 0.0010156956057486067],
    [945.7381465328102, 0.001064998384616531],
    [950.4275263061841, 0.0011121761462764025],
    [955.1169060795579, 0.001154245285099342],
    [959.8062858529318, 0.00118971461823054],
    [964.4956656263056, 0.0012185399992181605],
    [969.1850453996794, 0.0012419142281655257],
    [973.8744251730533, 0.0012619075152920415],
    [978.5638049464271, 0.0012810105235035235],
    [983.253184719801, 0.001301656893755252],
    [987.9425644931748, 0.001325804948281489],
    [992.6319442665487, 0.0013546385592031823],
    [997.3213240399225, 0.0013884138787465903],
    [1002.0107038132963, 0.0014264458276387493],
    [1006.7000835866702, 0.001467208661013433],
    [1011.389463360044, 0.0015085235783734233],
    [1016.0788431334179, 0.0015478174163823272],
    [1020.7682229067917, 0.0015824460722377242],
    [1025.4576026801656, 0.0016100706743185401],
    [1030.1469824535393, 0.001629049635326776],
    [1034.8363622269133, 0.0016387763305573872],
    [1039.5257420002872, 0.0016398716983383457],
    [1044.215121773661, 0.0016341537047746154],
    [1048.9045015470347, 0.0016243568181729547],
    [1053.5938813204086, 0.0016136490318869763],
    [1058.2832610937826, 0.0016050611576245318],
    [1062.9726408671563, 0.001600972885409955],
    [1067.66202064053, 0.001602778528456662],
    [1072.351400413904, 0.0016107922373262947],
    [1077.040780187278, 0.0016243759087183887],
    [1081.7301599606517, 0.0016422145710885611],
    [1086.4195397340254, 0.001662643015749439],
    [1091.1089195073994, 0.0016839433237224415],
    [1095.7982992807733, 0.0017045691537367841],
    [1100.487679054147, 0.0017232882895550467],
    [1105.1770588275208, 0.0017392563802420581],
    [1109.8664386008948, 0.0017520405001833271],
    [1114.5558183742687, 0.0017616079646899513],
    [1119.2451981476424, 0.0017682911755076182],
    [1123.9345779210162, 0.001772735571019755],
    [1128.6239576943901, 0.0017758330103618907],
    [1133.313337467764, 0.0017786357099460182],
    [1138.0027172411378, 0.001782239584298517],
    [1142.6920970145115, 0.0017876277926460083],
    [1147.3814767878855, 0.0017954806201517161],
    [1152.0708565612595, 0.0018059833389046929],
    [1156.7602363346332, 0.0018186868583446496],
    [1161.449616108007, 0.0018324810042403127],
    [1166.1389958813809, 0.0018457179586654265],
    [1170.8283756547548, 0.0018564783047332525],
    [1175.5177554281286, 0.0018629220660482447],
    [1180.2071352015023, 0.0018636346997102054],
    [1184.8965149748763, 0.0018578786501298986],
    [1189.5858947482502, 0.001845694598213448],
    [1194.275274521624, 0.0018278469703771907],
    [1198.9646542949977, 0.0018056524596841471],
    [1203.6540340683716, 0.0017807504967033573],
    [1208.3434138417456, 0.0017548676003651786],
    [1213.0327936151193, 0.0017296050171448512],
    [1217.722173388493, 0.0017062588668223222],
    [1222.411553161867, 0.0016856761849969766],
    [1227.100932935241, 0.0016681577144370895],
    [1231.7903127086147, 0.0016534270928544796],
    [1236.4796924819884, 0.0016406832181133522],
    [1241.1690722553624, 0.0016287341819071763],
    [1245.8584520287363, 0.0016161856520699772],
    [1250.54783180211, 0.0016016387041725626],
    [1255.2372115754838, 0.0015838526867152085],
    [1259.9265913488578, 0.0015618475933992786],
    [1264.6159711222317, 0.0015349472208424232],
    [1269.3053508956054, 0.0015027856350963659],
    [1273.9947306689792, 0.0014653064309688077],
    [1278.6841104423531, 0.0014227763695105337],
    [1283.373490215727, 0.0013758178519090469],
    [1288.0628699891008, 0.0013254459105002522],
    [1292.7522497624745, 0.0012730815042926974],
    [1297.4416295358485, 0.0012205087131302893],
    [1302.1310093092225, 0.0011697520281543571],
    [1306.8203890825962, 0.0011228715167681605],
    [1311.50976885597, 0.0010817033561769818],
    [1316.1991486293439, 0.0010476006313978787],
    [1320.8885284027178, 0.0010212414570260123],
    [1325.5779081760916, 0.001002559363082945],
    [1330.2672879494653, 0.000990815418991877],
    [1334.9566677228393, 0.0009847852934235207],
    [1339.6460474962132, 0.0009829967454387707],
    [1344.335427269587, 0.0009839409869830938],
    [1349.0248070429607, 0.0009862009776849924],
    [1353.7141868163346, 0.0009884824932412334],
    [1358.4035665897086, 0.000989580388310484],
    [1363.0929463630823, 0.0009883416730761122],
    [1367.782326136456, 0.0009836856400922717],
    [1372.47170590983, 0.0009747100059688368],
    [1377.161085683204, 0.0009608649535086234],
    [1381.8504654565777, 0.0009421351852440726],
    [1386.5398452299514, 0.0009191525346739372],
    [1391.2292250033254, 0.000893176887340656],
    [1395.9186047766993, 0.0008659256829003274],
    [1400.607984550073, 0.000839284978503604],
    [1405.2973643234468, 0.0008149766089411218],
    [1409.9867440968208, 0.000794270102882864],
    [1414.6761238701947, 0.0007778103475653692],
    [1419.3655036435684, 0.0007655912935235529],
    [1424.0548834169422, 0.0007570593407151102],
    [1428.7442631903161, 0.0007512948579728723],
    [1433.43364296369, 0.0007472072807893312],
    [1438.1230227370638, 0.0007436894995603207],
    [1442.8124025104376, 0.0007397037681244788],
    [1447.5017822838115, 0.0007343030247596722],
    [1452.1911620571855, 0.0007266172357643324],
    [1456.8805418305592, 0.000715845930222032],
    [1461.569921603933, 0.0007012918607708132],
    [1466.2593013773069, 0.0006824486923839881],
    [1470.9486811506808, 0.0006591255736198397],
    [1475.6380609240546, 0.0006315653133889183],
    [1480.3274406974283, 0.000600502737806621],
    [1485.0168204708023, 0.0005671224438120136],
    [1489.7062002441762, 0.0005329079307045869],
    [1494.39558001755, 0.0004994145626810122],
    [1499.0849597909237, 0.000468029563403939],
    [1503.7743395642976, 0.00043978881504034173],
    [1508.4637193376716, 0.0004152985118118359],
    [1513.1530991110453, 0.00039476862249566004],
    [1517.842478884419, 0.0003781228390922118],
    [1522.531858657793, 0.0003651250461590632],
    [1527.221238431167, 0.00035546559317396965],
    [1531.9106182045407, 0.00034877859705096146],
    [1536.5999979779144, 0.0003445995368937363],
    [1541.2893777512884, 0.000342302314745894],
    [1545.9787575246623, 0.0003410641223472102],
    [1550.668137298036, 0.00033989337467359604],
    [1555.3575170714098, 0.0003377294124923949],
    [1560.0468968447838, 0.000333595613615371],
    [1564.7362766181577, 0.0003267700480581557],
    [1569.4256563915314, 0.0003169330890762056],
    [1574.1150361649052, 0.00030425676086675874],
    [1578.8044159382791, 0.000289411517650525],
    [1583.493795711653, 0.00027347987850591306],
    [1588.1831754850268, 0.00025778265196067465],
    [1592.8725552584006, 0.0002436423425228277],
    [1597.5619350317745, 0.00023212703411124507],
    [1602.2513148051485, 0.00022383049773455383],
    [1606.9406945785222, 0.00021874345811008286],
    [1611.630074351896, 0.00021625320774537818],
    [1616.3194541252699, 0.0002152770176811903],
    [1621.0088338986438, 0.00021449858083824003],
    [1625.6982136720176, 0.00021264849892815466],
    [1630.3875934453913, 0.00020875956576871376],
    [1635.0769732187653, 0.00020233841129993003],
    [1639.7663529921392, 0.00019342203736546335],
    [1644.455732765513, 0.0001825205121082127],
    [1649.1451125388867, 0.0001704744894641582],
    [1653.8344923122606, 0.00015827093990070578],
    [1658.5238720856346, 0.00014686105452879525],
    [1663.2132518590083, 0.0001370142761977179],
    [1667.902631632382, 0.00012922758815293287],
    [1672.592011405756, 0.00012369462445091297],
    [1677.28139117913, 0.0001203277121184428],
    [1681.9707709525037, 0.00011881844059025998],
    [1686.6601507258774, 0.00011871866685823939],
    [1691.3495304992514, 0.00011952381042080227],
    [1696.0389102726253, 0.00012074363576918948],
    [1700.728290045999, 0.00012195168905516999],
    [1705.4176698193728, 0.00012281153953160444],
    [1710.1070495927468, 0.000123083912311393],
    [1714.7964293661207, 0.00012262205446894237],
    [1719.4858091394944, 0.00012136277263225846],
    [1724.1751889128682, 0.00011931822471651468],
    [1728.8645686862421, 0.0001165700378937673],
    [1733.553948459616, 0.00011326387539413626],
    [1738.2433282329898, 0.00010960009443710029],
    [1742.9327080063636, 0.00010581541754455061],
    [1747.6220877797375, 0.00010215234458844717],
    [1752.3114675531115, 0.00009881764556524636],
    [1757.0008473264852, 0.000095937682183307],
    [1761.690227099859, 0.00009352385267017641],
    [1766.379606873233, 0.00009146270165822765],
    [1771.0689866466068, 0.00008953993211741706],
    [1775.7583664199806, 0.00008749652364335745]
  ];
  const data = dataValue;
  const intepretationVal = data.interpretation;

  const interpretation = setOr(intepretationVal, 'Normal');
  if (data.section === 'mental_health') {
    density_plot = mental_plot;
  }
  if (data.section === 'nutrition') {
    if (data.key === 'vegetables') {
      density_plot = vegetable_plot;
    } else if (data.key === 'added_sugars') {
      density_plot = added_sugars_plot;
    } else if (data.key === 'fiber') {
      density_plot = fiber_plot;
    } else if (data.key === 'fruits') {
      density_plot = fruits_plot;
    } else if (data.key === 'dairy') {
      density_plot = dairy_plot;
    } else if (data.key === 'whole_grains') {
      density_plot = whole_grains_plot;
    } else if (data.key === 'calcium') {
      density_plot = calcium_plot;
    }
  }
  if (data.section === 'balance') {
    // this.density_plot = this.visceral_plot
  }
  if (data.section === 'riskscore') {
    // this.density_plot = this.visceral_plot
  }

  const ranges = setOr(data.ranges, [
    {
      interpretation: interpretation,
      color: '#000000',
      min: 0,
      max: 100
    }
  ]);
  // Object.assign(ranges[0], {color:'red'})

  const getPlotPercentage = function(
    section,
    value,
    minIndex,
    maxIndex,
    decimalRequired = false,
    skipValidation = false
  ) {
    // added for body scan height
    if ( section === 'height'){
      if (value === '5.10' || value === '5.11') {
        value = '5.99'
      }
    }

    // min validation
    if (
      (Number(value) === 0 || Number(value) < minIndex) &&
      !skipValidation
    ) {
      return 0
    }

    let tempVar
    // cheking if decimal is required, as we need to consider decimals for exact plotting of somepoints ex: temprature - vital_signs
    if (decimalRequired) {
      tempVar = value || 0
    } else {
      tempVar =
        Math.round(Number(value)) >= minIndex
          ? Math.round(Number(value))
          : minIndex
    }
    // max validation
    if (tempVar > maxIndex && !skipValidation) {
      return 99
    }
    // indicator position calculation (in %) based on category - ranges
    switch (section) {
      // Nutrition Section
      case 'vegetables':
        if (tempVar >= 1.8) {
          tempVar = Math.ceil((tempVar - minIndex) * 53)
        } else {
          tempVar = Math.ceil((tempVar - minIndex) * 63)
        }
        return tempVar >= 100 ? 99 : tempVar
      case 'added_sugars':
      case 'fruits':
        tempVar = Math.round((tempVar - minIndex) * 2.32)
        return tempVar >= 100 ? 99 : tempVar
      case 'dairy':
        const minCount = 35
        const l1 = Math.floor(tempVar)
        if (tempVar > 2.0) {
          tempVar = (tempVar - minIndex) * (minCount + l1)
        } else if (tempVar > 1.5) {
          tempVar = (tempVar - minIndex) * minCount
        } else {
          tempVar = (tempVar - minIndex) * 32
        }
        return tempVar >= 100 ? 99 : tempVar
      case 'whole_grains':
        tempVar = (tempVar - minIndex) * 100
        return tempVar >= 100 ? 99 : tempVar
      case 'calcium':
        if (tempVar < 842.6) {
          return 0
        } else if (tempVar < 1030.2) {
          return 20
        } else if (tempVar < 1217.8) {
          return 40
        } else if (tempVar < 1405.3) {
          return 60
        } else if (tempVar < 1592.9) {
          return 80
        } else if (tempVar > 1592.9) {
          return 99
        }
        return 99
      case 'fiber':
        if (tempVar > 17) {
          if (tempVar >= 24 && tempVar < 28) {
            tempVar = (tempVar - minIndex) * 6
          } else {
            tempVar = (tempVar - minIndex) * 5.7
          }
        } else {
          tempVar = (tempVar - minIndex) * 6.5
        }
        return tempVar >= 100 ? 99 : tempVar
      // Nutrition Section

      case 'balance':
        tempVar = (tempVar - minIndex + 5) * 2 - 10
        return tempVar >= 100 ? 99 : tempVar
      case 'riskscore':
        const newVal = parseFloat(value) * 10
        return newVal >=100 ? 99 : newVal
      case 'mental_health':
        tempVar = tempVar * 7
        if (value > 10) {
          tempVar = tempVar + 1
        }
        return tempVar >= 100 ? 99 : tempVar
      case 'oxygen_saturation':
        return tempVar
      case 'temperature':
        tempVar = (tempVar - minIndex) * 10
        return tempVar >= 100 ? 99 : tempVar
      case 'height':
        tempVar = (tempVar - minIndex) * 100
        tempVar = tempVar >= 100 ? 99 : tempVar
        return tempVar >= 100 ? 99 : Math.round(tempVar)
      case 'weight':
        tempVar = tempVar - minIndex
        const x = tempVar / 37
        tempVar = tempVar - 17 * x
        return tempVar >= 100 ? 99 : Math.round(tempVar)
      case 'waist_circumference':
        tempVar = (tempVar - minIndex) * 4
        return tempVar >= 100 ? 99 : tempVar
      case 'waist_to_hip_ratio':
        tempVar = (tempVar - minIndex) * 220
        return tempVar >= 100 ? 99 : tempVar
      case 'trunk_to_leg_volume_ratio':
        if (tempVar < 1.5) {
          tempVar = (tempVar - minIndex) * 100
        } else {
          // as there is difference in range between - no even ranges given > 1.5
          tempVar = (tempVar - minIndex) * 100 + 10
        }
        return tempVar >= 100 ? 99 : tempVar
      case 'body_fat_mass':
        tempVar = (tempVar - minIndex) * 3.3
        return tempVar >= 100 ? 99 : tempVar
      case 'skeletal_muscle_mass':
        if (tempVar >= 20 && tempVar <= 33) {
          tempVar = (tempVar - minIndex) * 1.35
        } else if (tempVar > 33 && tempVar <= 37) {
          tempVar = (tempVar - minIndex) * 2.3
        } else if (tempVar > 37 && tempVar <= 41) {
          tempVar = (tempVar - minIndex) * 2.8
        } else if (tempVar > 41 && tempVar <= 45) {
          tempVar = (tempVar - minIndex) * 3.2
        } else if (tempVar > 45 && tempVar <= 49) {
          tempVar = (tempVar - minIndex) * 3.45
        }
        return tempVar >= 100 ? 99 : tempVar
      case 'visceral_fat_level':
        if (tempVar <= 9) {
          tempVar = (tempVar - minIndex) * 5
        } else {
          tempVar = (tempVar - minIndex) * 5.3
        }
        return tempVar >= 100 ? 99 : tempVar
      case 'strength':
      case 'dexterity':
        tempVar = (tempVar - minIndex) * 2.5
        return tempVar >= 100 ? 99 : tempVar

      default:
        return 0
    }
  }

  let value = setOr(data.chartVal || data.value, 0)
    if (data.section === 'balance') {
      value = `${getPlotPercentage(
        data.section,
        data.chartVal,
        50,
        99
      )}%`
    } else if (data.section === 'riskscore') {
      value = `${getPlotPercentage(
        data.section,
        data.chartVal,
        0,
        10
      )}%`
    } else if (data.section === 'cognition') {
      value = `${data.chartVal}%`
      if (data.chartVal >= 100) {
        value = `99%`
      }
      if (data.chartVal <= 0) {
        value = `0%`
      }
    } else if (data.key === 'body_fat_mass') {
      value = `${getPlotPercentage(data.key, data.value, 9, 39)}%`
    } else if (data.key === 'skeletal_muscle_mass') {
      value = `${getPlotPercentage(data.key, data.value, 20, 49)}%`
    } else if (data.key === 'visceral_fat_level') {
      value = `${getPlotPercentage(data.key, data.chartVal, 1, 20)}%`
    } else if (data.key === 'heart_rate') {
      value = `${getPlotPercentage(
        'balance',
        data.chartVal,
        50,
        99,
        true
      )}%`
    } else if (data.key === 'temperature') {
      value = `${getPlotPercentage(
        data.key,
        data.chartVal,
        95,
        105,
        true
      )}%`
    } else if (data.key === 'oxygen_saturation') {
      value = `${getPlotPercentage(data.key, data.chartVal, 0, 99)}%`
    } else if (data.key === 'height') {
      value = `${getPlotPercentage(
        data.key,
        data.chartVal || data.value ,
        5.3,
        6.3,
        true
      )}%`
    } else if (data.key === 'weight') {
      value = `${getPlotPercentage(data.key, data.value, 125, 305)}%`
    } else if (data.key === 'waist_circumference') {
      value = `${getPlotPercentage(data.key, data.value, 30, 54)}%`
    } else if (data.key === 'waist_to_hip_ratio') {
     value = `${getPlotPercentage(
        data.key,
        data.chartVal,
        0.66,
        1.1,
        true
      )}%`
    } else if (data.key === 'trunk_to_leg_volume_ratio') {
      value = `${getPlotPercentage(
        data.key,
        data.chartVal,
        1.0,
        1.9,
        true
      )}%`
    } else if (data.section === 'strength' || data.section === 'dexterity') {
      value = `${getPlotPercentage(
        data.section,
        data.chartVal || data.value,
        30,
        70
      )}%`
    } else if (data.section === 'mental_health') {
      value = `${getPlotPercentage(data.section, data.value, 0, 14)}%`
    } else if (data.key === 'vegetables') {
      value = `${getPlotPercentage(
        data.key,
        data.value,
        1.1,
        3,
        true
      )}%`
    } else if (data.key === 'added_sugars' || data.key === 'fruits') {
      value = `${getPlotPercentage(data.key, data.value, 11, 53)}%`
    } else if (data.key === 'dairy') {
      value = `${getPlotPercentage(
        data.key,
        data.value,
        0.9,
        3.5,
        true
      )}%`
    } else if (data.key === 'whole_grains') {
      value = `${getPlotPercentage(
        data.key,
        data.value,
        1.2,
        2.2,
        true
      )}%`
    } else if (data.key === 'fiber' || data.key === 'calcium') {
      value = `${getPlotPercentage(
        data.key,
        data.value,
        14,
        32,
        null,
        true
      )}%`
    }

  density_plot =
    data.key === 'visceral_fat_level' || data.section === 'vital_sign'
      ? visceral_plot
      : density_plot;
  const plotPoints = setOr(density_plot, emptyPlot());
  plotPoints.length = 200;
  /* Testing if the plot points came as empty */

  if (every(plotPoints, plot => last(plot) === 0)) {
    console.log('empty plots', '', true);
  }

  const candidates = map(plotPoints, plot => first(plot));
  const minVal = min(candidates);
  const maxVal = max(candidates);

  x.domain([minVal, maxVal]);
  y.domain(d3.extent(plotPoints, plot => last(plot)));

  let ticks = [
    plotPoints[0][0],
    plotPoints[40][0],
    plotPoints[80][0],
    plotPoints[120][0],
    plotPoints[160][0],
    plotPoints[199][0]
  ]
  if (data.section === 'mental_health') {
    ticks = [0, 3, 6, 8, 11, 14]
  }

  let round = 0;
  if (maxVal <= 1) round = 2;
  if (maxVal - minVal <= 10) round = 1;
  const xAxis = d3.svg
    .axis()
    .scale(x)
    .orient('bottom')
    .tickValues(ticks)
    .tickSize(-height)

    .tickPadding(height / 3);

  if (data.section === 'cognition') {
    xAxis.tickFormat(d => {
      if (d === 0 || d < 1.1) {
        return 'Low';
      } else if (d === 40 || (d > 1.3 && d < 1.5)) {
        return 'Avg';
      } else if (d === 99.5 || d > 1.8) {
        return 'High';
      }
    });
  } else if (data.section === 'riskscore') {
    xAxis.tickFormat(d => {
      if (d > 0 && d < 1.02) {
        return '0%'
      } else if (d < 1.29) {
        return '2%'
      } else if (d < 1.4) {
        return '4%'
      } else if (d < 1.6) {
        return '6%'
      } else if (d < 1.8) {
        return '8%'
      } else if (d > 1.8) {
        return '10%'
      }
    })
  } else if (data.section === 'balance') {
    xAxis.tickFormat(d => {
      if (d > 0 && d < 1.02) {
        return '50'
      } else if (d < 1.29) {
        return '60'
      } else if (d < 1.4) {
        return '70'
      } else if (d < 1.6) {
        return '80'
      } else if (d < 1.8) {
        return '90'
      } else if (d > 1.8) {
        return '100'
      }
    })
  } else if (data.section === 'body_composition') {
    if (data.key === 'percent_body_fat' || data.key === 'body_fat_mass' ) {
        xAxis.tickFormat(d => {
          if (d > 1.0 && d < 1.1) {
            return '9'
          } else if (d < 1.37) {
            return '15'
          } else if (d < 1.5) {
            return '21'
          } else if (d < 1.7) {
            return '27'
          } else if (d < 1.9) {
            return '33'
          } else if (d > 1.9) {
            return '39'
          }
        })
    } else if (data.key === 'percent_skeletal_muscle' || data.key === 'skeletal_muscle_mass') {
      xAxis.tickFormat(d => {
        if (d > 1.0 && d < 1.1) {
          return '20'
        } else if (d < 1.3) {
          return '33'
        } else if (d < 1.5) {
          return '37'
        } else if (d < 1.7) {
          return '41'
        } else if (d < 1.9) {
          return '45'
        } else if (d > 1.9) {
          return '49'
        }
      })
    } else if (data.key === 'visceral_fat_level' ||
    data.section === 'vital_sign') {
      /* xAxis.tickFormat(d => {
      if (d > 1.0 && d < 1.1) {
        return '1'
      } else if (d < 1.1) {
        return '5'
      } else if (d < 1.2) {
        return '9'
      } else if (d < 1.4) {
        return '12'
      } else if (d < 1.8) {
        return '16'
      } else if (d > 1.8) {
        return '20'
      }
    }) */
    }
  } else if (data.section === 'vital_sign') {
    if (data.key === 'heart_rate') {
      xAxis.tickFormat(d => {
        if (d < 1.1) {
          return '50'
        } else if (d < 4.9) {
          return '60'
        } else if (d < 8.6) {
          return '70'
        } else if (d < 12.5) {
          return '80'
        } else if (d < 16.3) {
          return '90'
        } else if (d > 19) {
          return '100'
        }
      })
    } else if (data.key === 'oxygen_saturation') {
      xAxis.tickFormat(d => {
        if (d < 1.1) {
          return '0'
        } else if (d < 4.9) {
          return '20'
        } else if (d < 8.6) {
          return '40'
        } else if (d < 12.5) {
          return '60'
        } else if (d < 16.3) {
          return '80'
        } else if (d > 19) {
          return '100'
        }
      })
    } else if (data.key === 'temperature') {
        xAxis.tickFormat(d => {
          if (d < 1.1) {
            return '95'
          } else if (d < 4.9) {
            return '97'
          } else if (d < 8.6) {
            return '99'
          } else if (d < 12.5) {
            return '101'
          } else if (d < 16.3) {
            return '103'
          } else if (d > 19) {
            return '105'
          }
        })
    }
  } else if (data.section === 'nutrition') {
    if (data.key === 'vegetables') {
      xAxis.tickFormat(d => {
        if (d < 1.06) {
          return '1.1'
        } else if (d < 1.44) {
          return '1.4'
        } else if (d < 1.82) {
          return '1.8'
        } else if (d < 2.2) {
          return '2.2'
        } else if (d < 2.59) {
          return '2.6'
        } else if (d > 2.59) {
          return '3.0'
        }
      })
    } else if (data.key === 'added_sugars') {
      xAxis.tickFormat(d => {
        if (d < 10.91) {
          return '11'
        } else if (d < 19.4) {
          return '19'
        } else if (d < 27.9) {
          return '28'
        } else if (d < 36.4) {
          return '36'
        } else if (d < 44.9) {
          return '45'
        } else if (d > 44.9) {
          return '53'
        }
      })
    } else if (data.key === 'fiber') {
      xAxis.tickFormat(d => {
        if (d < 13.6) {
          return '14'
        } else if (d < 17.2) {
          return '17'
        } else if (d < 20.9) {
          return '21'
        } else if (d < 24.5) {
          return '24'
        } else if (d < 28.2) {
          return '28'
        } else if (d > 28.2) {
          return '32'
        }
      })
    } else if (data.key === 'fruits') {
      xAxis.tickFormat(d => {
        if (d < 0.421) {
          return '11'
        } else if (d < 0.88) {
          return '19'
        } else if (d < 1.34) {
          return '28'
        } else if (d < 1.79) {
          return '36'
        } else if (d < 2.25) {
          return '45'
        } else if (d > 2.25) {
          return '53'
        }
      })
    } else if (data.key === 'dairy') {
      xAxis.tickFormat(d => {
        if (d < 0.95) {
          return '0.9'
        } else if (d < 1.47) {
          return '1.5'
        } else if (d < 1.99) {
          return '2.0'
        } else if (d < 2.51) {
          return '2.5'
        } else if (d < 3.04) {
          return '3.0'
        } else if (d > 3.04) {
          return '3.5'
        }
      })
    } else if (data.key === 'whole_grains') {
      xAxis.tickFormat(d => {
        if (d < 1.21) {
          return '1.2'
        } else if (d < 1.41) {
          return '1.4'
        } else if (d < 1.62) {
          return '1.6'
        } else if (d < 1.83) {
          return '1.8'
        } else if (d < 2.04) {
          return '2.0'
        } else if (d > 2.04) {
          return '2.2'
        }
      })
    } else if (data.key === 'calcium') {
      xAxis.tickFormat(d => {
        if (d < 842.6) {
          return '14'
        } else if (d < 1030.2) {
          return '17'
        } else if (d < 1217.8) {
          return '21'
        } else if (d < 1405.3) {
          return '24'
        } else if (d < 1592.9) {
          return '28'
        } else if (d > 1592.9) {
          return '32'
        }
      })
    }
  } else if (data.section === 'anthropometrics') {
    if (data.key === 'height') {
      xAxis.tickFormat(d => {
        if (d < 1.02) {
          return '5.3'
        } else if (d < 1.2) {
          return '5.5'
        } else if (d < 1.38) {
          return '5.7'
        } else if (d < 1.55) {
          return '5.9'
        } else if (d < 1.73) {
          return '6.1'
        } else if (d > 1.73) {
          return '6.3'
        }
      })
    } else if (data.key === 'weight') {
      xAxis.tickFormat(d => {
        if (d < 1.02) {
          return '125'
        } else if (d < 1.2) {
          return '162'
        } else if (d < 1.38) {
          return '198'
        } else if (d < 1.55) {
          return '234'
        } else if (d < 1.73) {
          return '270'
        } else if (d > 1.9) {
          return '305'
        }
      })
    } else if (data.key === 'waist_circumference') {
      xAxis.tickFormat(d => {
        if (d < 1.02) {
          return '30'
        } else if (d < 1.2) {
          return '34'
        } else if (d < 1.38) {
          return '39'
        } else if (d < 1.55) {
          return '44'
        } else if (d < 1.73) {
          return '49'
        } else if (d > 1.9) {
          return '54'
        }
      })
    } else if (data.key === 'waist_to_hip_ratio') {
      xAxis.tickFormat(d => {
        if (d < 1.02) {
          return '0.66'
        } else if (d < 1.2) {
          return '0.75'
        } else if (d < 1.38) {
          return '0.84'
        } else if (d < 1.55) {
          return '0.93'
        } else if (d < 1.73) {
          return '1.02'
        } else if (d > 1.9) {
          return '1.10'
        }
      })
    } else if (data.key === 'trunk_to_leg_volume_ratio') {
      xAxis.tickFormat(d => {
        if (d < 1.02) {
          return '1.0'
        } else if (d < 1.2) {
          return '1.2'
        } else if (d < 1.38) {
          return '1.4'
        } else if (d < 1.55) {
          return '1.5'
        } else if (d < 1.73) {
          return '1.7'
        } else if (d > 1.9) {
          return '1.9'
        }
      })
    }
  } else if (data.section === 'strength' || data.section === 'dexterity') {
    xAxis.tickFormat(d => {
      if (d > 1.0 && d < 1.1) {
        return '30'
      } else if (d < 1.2) {
        return '38'
      } else if (d < 1.4) {
        return '46'
      } else if (d < 1.6) {
        return '54'
      } else if (d < 1.8) {
        return '62'
      } else if (d > 1.8) {
        return '70'
      }
    })
  }

  const yAxis = d3.svg
    .axis()
    .scale(y)
    .orient('left');

  const line = d3.svg
    .line()
    .x(dx => x(dx[0]))
    .y(dy => y(dy[1]));

  xAxisContainer.call(xAxis);

  yAxisContainer
    .call(yAxis)
    .select('.ticks')
    .remove();

  // Curve
  if (data.section !== 'balance' && data.section !== 'riskscore') {
  lineContainer
    .datum(plotPoints)
    .transition()
    .attr('d', line);
  }
  // Determine color and pick a range
    const color = data.color

  // Clamp value (always show an indicator, even if value is out of chart range)
  const clamped = value;

  // if (value <= minVal) {
  //   // console.log('smaller than min', { value: data.value, min: min })
  //   clamped = x(minVal) + 2;
  // }
  // if (value >= maxVal) {
  //   // console.log('bigger than max', { value: data.value, max: max })
  //   clamped = x(maxVal) - 10;
  // }

  const showIndicatorClass = 'indicator';

  svg
    .select('#' + divId + '_indicator')
    .transition()
    .attr('x', clamped)
    .attr('fill', color)
    .attr('class', showIndicatorClass);

  svg
    .select('.interpretation')
    .text(data.section === 'mental_health' ? '' : interpretation)
    .transition()
    .attr('fill', color);
}

d3.selection.prototype.moveToFront = function () {
  return this.each(function () {
    this.parentNode.appendChild(this)
  })
}
